

// import { useMemo, useState, useEffect } from 'react';
// import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import {
//   Bot, CheckCircle2, Send, Sparkles, Eye, Info,
//   Loader2, MessageCircleReply, Zap, Ban, DollarSign, Clock,
//   ShieldAlert, XCircle, Mail, User, ArrowDown, AlertTriangle,
//   Building2, RefreshCw, Search, Flame, TreePine, HelpCircle,
//   Coffee, Edit2, TriangleAlert, MousePointerClick, ExternalLink,
//   ShieldX, Activity, Wifi, WifiOff,
// } from 'lucide-react';
// import {
//   useSequenceEngagement,
//   isSequenceEngaged,
//   totalVisibleSeconds,
//   formatDwellTime,
// } from '@/features/engagement/useEngagement';
// import { toast } from 'sonner';

// import { http } from '@/services/apiClient';
// import { cn } from '@/lib/utils';
// import { Badge } from '@/components/ui/badge';
// import { Button } from '@/components/ui/button';
// import {
//   Card, CardContent, CardDescription, CardHeader, CardTitle,
// } from '@/components/ui/card';
// import {
//   Dialog, DialogContent, DialogDescription, DialogFooter,
//   DialogHeader, DialogTitle,
// } from '@/components/ui/dialog';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Switch } from '@/components/ui/switch';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { Textarea } from '@/components/ui/textarea';
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipProvider,
//   TooltipTrigger,
// } from '@/components/ui/tooltip';

// // ─── Types ────────────────────────────────────────────────────────────────────

// interface ReplyDraft {
//   id: string;
//   sequenceId: string;
//   prospectId: string;
//   originalReply: string;
//   category: string;
//   summary: string | null;
//   suggestedAction: string | null;
//   draftBody: string | null;
//   status: string;
//   sentAt: string | null;
//   autoPilotEligible: boolean;
//   confidence: number | null;
//   autoSentAt: string | null;
//   snoozedUntil: string | null;
//   wrongCompanyHandledAt: string | null;
//   extraData: string | null;
//   // v4 reclassification fields
//   manualCategory: string | null;
//   manualCategoryBy: string | null;
//   manualCategoryAt: string | null;
//   manualCategoryNote: string | null;
//   isHumanReviewed: boolean;
//   createdAt: string;
//   updatedAt: string;
//   // Enriched fields from backend join
//   prospectName: string | null;
//   prospectEmail: string | null;
//   sentEmailSubject: string | null;
//   sentEmailBody: string | null;
// }

// interface AutoPilotPreview {
//   eligible: ReplyDraft[];
//   count: number;
// }

// interface AutoPilotSendResult {
//   sent: number;
//   failed: number;
//   markedOnly: number;
//   total: number;
// }

// interface Prospect {
//   id: string;
//   firstName: string;
//   lastName: string;
//   company: string | null;
// }

// interface TrackingSummary {
//   period_days: number;
//   total_sent: number;
//   total_replied: number;
//   total_bounced: number;
//   reply_rate: number;
//   bounce_rate: number;
//   top_bounce_reasons: Array<{ reason: string; count: number }>;
//   by_status: Record<string, number>;
// }

// interface BouncedSequence {
//   id: string;
//   status: string;
//   bouncedAt: string | null;
//   bounceReason: string | null;
//   prospectId: string;
//   subjectLine: string | null;
//   touchNumber: number;
// }

// interface MessageBlockedSequence {
//   id: string;
//   status: string;
//   messageBlockedAt: string | null;
//   messageBlockedReason: string | null;
//   prospectId: string;
//   subjectLine: string | null;
//   touchNumber: number;
// }

// interface ClickedSequence {
//   id: string;
//   sequenceId: string;
//   touchNumber: number;
//   prospectId: string;
//   prospectName: string;
//   prospectEmail: string | null;
//   originalUrl: string;
//   clickedAt: string;
//   /** Phase C: 'human' | 'likely_scanner' | 'bot' */
//   clickQuality?: string;
// }

// interface InboxStats {
//   total: number;
//   hotLeads: number;
//   warmFollowups: number;
//   ooo: number;
//   negative: number;
//   wrongCompany: number;
//   bounce: number;
//   unsubscribe: number;
//   curious: number;
//   other: number;
//   actionRequired: number;
//   suppressed: number;
//   positiveReplyRate: number;
//   autoSent: number;
//   unacknowledgedSavAlerts: number;
// }

// interface SAVAlert {
//   id: string;
//   replyDraftId: string;
//   detectedAt: string;
//   acknowledgedAt: string | null;
//   acknowledgedBy: string | null;
//   notes: string | null;
//   severity: string;
// }

// interface SAVAlertsListResponse {
//   alerts: SAVAlert[];
//   unacknowledgedCount: number;
// }

// interface CategorizeResult {
//   category: string;
//   confidence: number;
//   summary: string;
//   suggestedAction: string;
//   promptVersion: string;
//   returnDate: string | null;
//   newCompany: string | null;
//   referralName: string | null;
//   bounceType: string | null;
// }

// interface ActionResult {
//   ok: boolean;
//   message: string;
//   actionTaken: string;
//   draftId: string | null;
// }

// interface ReclassifyResult {
//   ok: boolean;
//   message: string;
//   draftId: string;
//   effectiveCategory: string;
//   savAlertId: string | null;
// }

// // ─── Constants ────────────────────────────────────────────────────────────────

// const CATEGORY_CONFIG: Record<string, {
//   label: string;
//   icon: React.ReactNode;
//   bgColor: string;
//   borderColor: string;
//   textColor: string;
//   actionLabel: string;
//   actionVariant: 'default' | 'destructive' | 'outline' | 'secondary';
//   playbook: string[];
// }> = {
//   hot_lead: {
//     label: 'Hot Lead',
//     icon: <Flame className="h-3 w-3" />,
//     bgColor: 'bg-emerald-50',
//     borderColor: 'border-emerald-200',
//     textColor: 'text-emerald-700',
//     actionLabel: 'Create Deal & Approve',
//     actionVariant: 'default',
//     playbook: ['Reply within 2 hours', 'Send calendar link immediately', 'Enter into CRM', 'Remove from sequence'],
//   },
//   warm_followup: {
//     label: 'Warm Follow-Up',
//     icon: <TreePine className="h-3 w-3" />,
//     bgColor: 'bg-teal-50',
//     borderColor: 'border-teal-200',
//     textColor: 'text-teal-700',
//     actionLabel: 'Approve & Continue Sequence',
//     actionVariant: 'default',
//     playbook: ['Answer any questions asked', 'Ask one qualifying question back', 'Continue the sequence'],
//   },
//   curious: {
//     label: 'Curious',
//     icon: <HelpCircle className="h-3 w-3" />,
//     bgColor: 'bg-blue-50',
//     borderColor: 'border-blue-200',
//     textColor: 'text-blue-700',
//     actionLabel: 'Add Value & Continue',
//     actionVariant: 'default',
//     playbook: ['Acknowledge briefly (1-2 sentences)', 'Add value with one specific detail', 'Continue in sequence'],
//   },
//   ooo: {
//     label: 'Out of Office',
//     icon: <Coffee className="h-3 w-3" />,
//     bgColor: 'bg-sky-50',
//     borderColor: 'border-sky-200',
//     textColor: 'text-sky-700',
//     actionLabel: 'Snooze to Return Date',
//     actionVariant: 'secondary',
//     playbook: ['Pause all sequences for this prospect', 'Resume 2-3 days after return date'],
//   },
//   negative: {
//     label: 'Not Interested',
//     icon: <XCircle className="h-3 w-3" />,
//     bgColor: 'bg-red-50',
//     borderColor: 'border-red-200',
//     textColor: 'text-red-700',
//     actionLabel: 'Suppress Prospect',
//     actionVariant: 'destructive',
//     playbook: ['Reply briefly if soft no', 'Suppress from this and all future campaigns', 'Never argue or pressure'],
//   },
//   bounce: {
//     label: 'Bounce',
//     icon: <Zap className="h-3 w-3" />,
//     bgColor: 'bg-orange-50',
//     borderColor: 'border-orange-200',
//     textColor: 'text-orange-700',
//     actionLabel: 'Remove from List',
//     actionVariant: 'destructive',
//     playbook: ['Hard bounce: remove permanently', 'Soft bounce: pause 7 days, retry once'],
//   },
//   wrong_company: {
//     label: 'Wrong Company',
//     icon: <Building2 className="h-3 w-3" />,
//     bgColor: 'bg-purple-50',
//     borderColor: 'border-purple-200',
//     textColor: 'text-purple-700',
//     actionLabel: 'Mark & Suppress',
//     actionVariant: 'secondary',
//     playbook: ['Suppress this prospect', 'Find their replacement', 'Add referral as new prospect if named'],
//   },
//   unsubscribe: {
//     label: 'Unsubscribe',
//     icon: <Ban className="h-3 w-3" />,
//     bgColor: 'bg-rose-50',
//     borderColor: 'border-rose-300',
//     textColor: 'text-rose-700',
//     actionLabel: 'Withdraw Consent',
//     actionVariant: 'destructive',
//     playbook: ['Do not reply', 'Suppress immediately from all campaigns', 'This action is permanent (GDPR)'],
//   },
//   action_required: {
//     label: 'Action Required',
//     icon: <TriangleAlert className="h-3 w-3" />,
//     bgColor: 'bg-amber-50',
//     borderColor: 'border-amber-300',
//     textColor: 'text-amber-800',
//     actionLabel: 'Acknowledge & Act',
//     actionVariant: 'secondary',
//     playbook: [
//       'This is a Sender Address Verification (SAV) challenge — not a prospect reply',
//       'Check your SPF/DKIM/DMARC records',
//       'Contact your domain or mail provider if deliverability is blocked',
//       'Acknowledge the alert once you have taken action',
//     ],
//   },
//   suppressed: {
//     label: 'Suppressed',
//     icon: <Ban className="h-3 w-3" />,
//     bgColor: 'bg-red-50',
//     borderColor: 'border-red-300',
//     textColor: 'text-red-800',
//     actionLabel: 'Already Suppressed',
//     actionVariant: 'destructive',
//     playbook: ['Prospect has been manually suppressed', 'No further outreach will be sent', 'Contact is excluded from all future campaigns'],
//   },
//   other: {
//     label: 'Uncategorized',
//     icon: <MessageCircleReply className="h-3 w-3" />,
//     bgColor: 'bg-gray-50',
//     borderColor: 'border-gray-200',
//     textColor: 'text-gray-600',
//     actionLabel: 'Approve for Manual Review',
//     actionVariant: 'outline',
//     playbook: ['Review manually', 'Re-classify if needed'],
//   },
// };

// const LEGACY_CATEGORY_DISPLAY: Record<string, string> = {
//   interested: 'hot_lead',
//   meeting_request: 'hot_lead',
//   demo_request: 'hot_lead',
//   positive_reply: 'warm_followup',
//   positive_signal: 'warm_followup',
//   positive: 'warm_followup',
//   negative_reply: 'negative',
//   not_interested: 'negative',
//   out_of_office: 'ooo',
//   oof: 'ooo',
//   needs_info: 'curious',
//   counter_proposal: 'curious',
//   neutral: 'curious',
// };

// const CATEGORY_COLORS: Record<string, string> = {
//   // v2 names
//   hot_lead:      'bg-emerald-100 text-emerald-700',
//   warm_followup: 'bg-teal-100 text-teal-700',
//   curious:       'bg-blue-100 text-blue-700',
//   ooo:           'bg-sky-100 text-sky-700',
//   negative:      'bg-red-100 text-red-700',
//   bounce:        'bg-orange-100 text-orange-700',
//   wrong_company: 'bg-purple-100 text-purple-700',
//   unsubscribe:   'bg-rose-100 text-rose-700',
//   other:         'bg-gray-100 text-gray-700',
//   // v4 human-only categories
//   action_required: 'bg-amber-100 text-amber-800',
//   suppressed:      'bg-red-100 text-red-800',
//   // legacy names
//   interested:      'bg-emerald-100 text-emerald-700',
//   positive_signal: 'bg-green-100 text-green-700',
//   meeting_request: 'bg-blue-100 text-blue-700',
//   needs_info:      'bg-amber-100 text-amber-700',
//   counter_proposal:'bg-purple-100 text-purple-700',
//   neutral:         'bg-gray-100 text-gray-700',
//   out_of_office:   'bg-sky-100 text-sky-700',
//   not_interested:  'bg-red-100 text-red-700',
//   positive:        'bg-emerald-100 text-emerald-700',
//   objection:       'bg-amber-100 text-amber-700',
//   oof:             'bg-sky-100 text-sky-700',
// };

// const STATUS_COLORS: Record<string, string> = {
//   pending:        'bg-amber-100 text-amber-700',
//   pending_review: 'bg-orange-100 text-orange-700',
//   approved:       'bg-blue-100 text-blue-700',
//   sent:           'bg-emerald-100 text-emerald-700',
//   dismissed:      'bg-gray-100 text-gray-600',
//   auto_sent:      'bg-violet-100 text-violet-700',
//   handled:        'bg-emerald-100 text-emerald-700',
// };

// const DRAFT_STATUS_LABEL: Record<string, string> = {
//   pending:        'Pending',
//   pending_review: 'Pending Review',
//   approved:       'Approved',
//   sent:           'Sent',
//   dismissed:      'Dismissed',
//   auto_sent:      'Auto Sent',
//   handled:        'Handled',
// };

// const POSITIVE_CATEGORIES = [
//   'interested', 'meeting_request', 'positive_signal', 'positive',
//   'hot_lead', 'warm_followup',
// ];

// const FILTER_PILLS: Array<{ key: string; label: string }> = [
//   { key: 'all',            label: 'All' },
//   { key: 'hot_lead',       label: '🔥 Hot Lead' },
//   { key: 'warm_followup',  label: '🌱 Warm' },
//   { key: 'curious',        label: '🤔 Curious' },
//   { key: 'ooo',            label: '☕ OOO' },
//   { key: 'negative',       label: '❌ Negative' },
//   { key: 'bounce',         label: '⚡ Bounce' },
//   { key: 'wrong_company',  label: '🏢 Wrong Co.' },
//   { key: 'unsubscribe',    label: '🚫 Unsub' },
//   { key: 'action_required',label: '⚠️ Action Req.' },
//   { key: 'suppressed',     label: '🔇 Suppressed' },
//   { key: 'other',          label: 'Other' },
// ];

// // Categories selectable in the override dropdown
// const RECLASSIFY_OPTIONS: Array<{ value: string; label: string; group: 'human' | 'ai' }> = [
//   // Human-only (highlighted)
//   { value: 'action_required', label: '⚠️ Action Required (SAV / Infrastructure)', group: 'human' },
//   { value: 'suppressed',      label: '🔇 Mark as Suppressed',                      group: 'human' },
//   // AI corrections
//   { value: 'hot_lead',        label: '🔥 Hot Lead',       group: 'ai' },
//   { value: 'warm_followup',   label: '🌱 Warm Follow-Up', group: 'ai' },
//   { value: 'curious',         label: '🤔 Curious',        group: 'ai' },
//   { value: 'ooo',             label: '☕ Out of Office',   group: 'ai' },
//   { value: 'negative',        label: '❌ Not Interested',  group: 'ai' },
//   { value: 'bounce',          label: '⚡ Bounce',          group: 'ai' },
//   { value: 'wrong_company',   label: '🏢 Wrong Company',  group: 'ai' },
//   { value: 'unsubscribe',     label: '🚫 Unsubscribe',    group: 'ai' },
//   { value: 'other',           label: 'Uncategorized',      group: 'ai' },
// ];

// function normalise<T>(data: unknown): T[] {
//   if (!data) return [];
//   if (Array.isArray(data)) return data as T[];
//   return ((data as { items?: T[] }).items) ?? [];
// }

// function getEffectiveCategory(draft: ReplyDraft): string {
//   return draft.manualCategory || draft.category;
// }

// function getCfg(category: string) {
//   const normalised = LEGACY_CATEGORY_DISPLAY[category] ?? category;
//   return CATEGORY_CONFIG[normalised] ?? CATEGORY_CONFIG['other'];
// }

// function parseExtra(extraData: string | null): Record<string, string> {
//   if (!extraData) return {};
//   try { return JSON.parse(extraData); } catch { return {}; }
// }

// // ─── Conversation Thread Component ────────────────────────────────────────────

// function ConversationThread({ draft }: { draft: ReplyDraft }) {
//   const prospectLabel = draft.prospectName ?? draft.prospectEmail ?? 'Prospect';

//   return (
//     <div className="space-y-3">
//       <div className="flex flex-col gap-1">
//         <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
//           <Mail className="h-3 w-3" /> You sent
//         </div>
//         <div className="rounded-lg border bg-muted/30 p-3 text-sm">
//           {draft.sentEmailSubject && (
//             <p className="font-medium text-xs text-muted-foreground mb-1.5">
//               Subject: {draft.sentEmailSubject}
//             </p>
//           )}
//           {draft.sentEmailBody ? (
//             (() => {
//               const isHtml = /<[a-z][\s\S]*>/i.test(draft.sentEmailBody!);
//               return isHtml ? (
//                 <div
//                   className="text-sm leading-relaxed prose prose-sm max-w-none [&_a]:text-blue-600 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_strong]:font-semibold [&_p]:mb-2 [&_li]:mb-1"
//                   dangerouslySetInnerHTML={{ __html: draft.sentEmailBody! }}
//                 />
//               ) : (
//                 <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.sentEmailBody}</p>
//               );
//             })()
//           ) : (
//             <span className="italic text-muted-foreground text-sm">
//               Email body not captured — check Sequence details.
//             </span>
//           )}
//         </div>
//       </div>

//       <div className="flex items-center justify-center text-muted-foreground/40">
//         <ArrowDown className="h-4 w-4" />
//       </div>

//       <div className="flex flex-col gap-1">
//         <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
//           <User className="h-3 w-3" />
//           <span>{prospectLabel} replied</span>
//           {draft.prospectEmail && (
//             <span className="normal-case font-normal text-muted-foreground/60">
//               &lt;{draft.prospectEmail}&gt;
//             </span>
//           )}
//         </div>
//         <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
//           <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.originalReply}</p>
//         </div>
//       </div>

//       {draft.draftBody && (
//         <div className="flex items-center justify-center text-muted-foreground/40">
//           <ArrowDown className="h-4 w-4" />
//         </div>
//       )}

//       {draft.draftBody && (
//         <div className="flex flex-col gap-1">
//           <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
//             <Sparkles className="h-3 w-3 text-emerald-600" />
//             <span className="text-emerald-700">AI-Generated Draft</span>
//             {draft.suggestedAction && (
//               <span className="normal-case font-normal text-muted-foreground/60 ml-1">
//                 · {draft.suggestedAction}
//               </span>
//             )}
//           </div>
//           <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
//             <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.draftBody}</p>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// // ─── Category Badge with Audit Tooltip ───────────────────────────────────────

// function CategoryBadge({ draft, size = 'sm' }: { draft: ReplyDraft; size?: 'sm' | 'xs' }) {
//   const effectiveCat = getEffectiveCategory(draft);
//   const cfg = getCfg(effectiveCat);
//   const isHumanSet = !!draft.manualCategory;
//   const px = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';

//   const badge = (
//     <span className={cn(px, 'rounded font-medium flex items-center gap-1', CATEGORY_COLORS[effectiveCat] ?? 'bg-gray-100')}>
//       {isHumanSet && <Edit2 className="h-2.5 w-2.5 opacity-60" />}
//       {cfg.label}
//     </span>
//   );

//   if (!isHumanSet) return badge;

//   return (
//     <TooltipProvider>
//       <Tooltip>
//         <TooltipTrigger asChild>{badge}</TooltipTrigger>
//         <TooltipContent className="max-w-[260px] text-xs space-y-1">
//           <p className="font-semibold">Manually reviewed</p>
//           {draft.manualCategoryAt && (
//             <p className="text-muted-foreground">
//               Set on {new Date(draft.manualCategoryAt).toLocaleString()}
//             </p>
//           )}
//           {draft.manualCategoryNote && (
//             <p className="italic">{draft.manualCategoryNote}</p>
//           )}
//         </TooltipContent>
//       </Tooltip>
//     </TooltipProvider>
//   );
// }

// // ─── SAV Alert Banner ─────────────────────────────────────────────────────────

// function SAVAlertBanner({
//   count,
//   onViewAlerts,
// }: {
//   count: number;
//   onViewAlerts: () => void;
// }) {
//   if (count === 0) return null;
//   return (
//     <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-center gap-3">
//       <TriangleAlert className="h-4 w-4 text-amber-700 shrink-0" />
//       <div className="flex-1 min-w-0">
//         <p className="text-sm font-semibold text-amber-900">
//           Action Required: {count} sender verification challenge{count === 1 ? '' : 's'} detected
//         </p>
//         <p className="text-xs text-amber-700 mt-0.5">
//           These are infrastructure emails — not prospect replies. Review and acknowledge each one.
//         </p>
//       </div>
//       <Button
//         size="sm"
//         variant="outline"
//         className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
//         onClick={onViewAlerts}
//       >
//         View Alerts →
//       </Button>
//     </div>
//   );
// }

// // ─── SAV Alerts Modal ─────────────────────────────────────────────────────────

// function SAVAlertsModal({
//   open,
//   onClose,
//   alerts,
//   onAcknowledge,
//   acknowledging,
// }: {
//   open: boolean;
//   onClose: () => void;
//   alerts: SAVAlert[];
//   onAcknowledge: (alertId: string, notes: string) => void;
//   acknowledging: string | null;
// }) {
//   const [notesMap, setNotesMap] = useState<Record<string, string>>({});

//   return (
//     <Dialog open={open} onOpenChange={onClose}>
//       <DialogContent className="sm:max-w-2xl">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <TriangleAlert className="h-4 w-4 text-amber-600" />
//             Sender Verification Alerts
//           </DialogTitle>
//           <DialogDescription>
//             These emails are Sender Address Verification (SAV) challenges from mail servers — not replies from prospects.
//             Review each one and acknowledge once you have taken the required action.
//           </DialogDescription>
//         </DialogHeader>
//         <div className="max-h-[60vh] overflow-y-auto space-y-3">
//           {alerts.length === 0 ? (
//             <div className="flex flex-col items-center justify-center py-8 text-center">
//               <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-2" />
//               <p className="text-sm font-medium">All alerts acknowledged</p>
//             </div>
//           ) : alerts.map((alert) => (
//             <div
//               key={alert.id}
//               className={cn(
//                 'rounded-lg border p-4 space-y-3',
//                 alert.severity === 'critical'
//                   ? 'border-red-300 bg-red-50'
//                   : 'border-amber-200 bg-amber-50/50',
//                 alert.acknowledgedAt && 'opacity-60'
//               )}
//             >
//               <div className="flex items-center justify-between gap-2">
//                 <div className="flex items-center gap-2">
//                   <Badge
//                     className={cn(
//                       'text-[10px]',
//                       alert.severity === 'critical'
//                         ? 'bg-red-100 text-red-800 border-red-300'
//                         : 'bg-amber-100 text-amber-800 border-amber-300'
//                     )}
//                   >
//                     {alert.severity === 'critical' ? '🔴 Critical' : '🟡 Warning'}
//                   </Badge>
//                   <span className="text-xs text-muted-foreground">
//                     Detected {new Date(alert.detectedAt).toLocaleString()}
//                   </span>
//                 </div>
//                 {alert.acknowledgedAt && (
//                   <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
//                     <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Acknowledged
//                   </Badge>
//                 )}
//               </div>
//               {alert.notes && (
//                 <p className="text-xs text-muted-foreground italic">{alert.notes}</p>
//               )}
//               {!alert.acknowledgedAt && (
//                 <div className="space-y-2">
//                   <Textarea
//                     rows={2}
//                     className="text-xs"
//                     placeholder="Notes on action taken (e.g. updated SPF record, whitelisted IP)…"
//                     value={notesMap[alert.id] ?? ''}
//                     onChange={(e) => setNotesMap((m) => ({ ...m, [alert.id]: e.target.value }))}
//                   />
//                   <Button
//                     size="sm"
//                     variant="outline"
//                     className="border-amber-400 text-amber-800 hover:bg-amber-100"
//                     onClick={() => onAcknowledge(alert.id, notesMap[alert.id] ?? '')}
//                     disabled={acknowledging === alert.id}
//                   >
//                     {acknowledging === alert.id
//                       ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
//                       : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
//                     }
//                     Acknowledge
//                   </Button>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//         <DialogFooter>
//           <Button variant="outline" onClick={onClose}>Close</Button>
//         </DialogFooter>
//       </DialogContent>
//     </Dialog>
//   );
// }

// // ─── Reclassify Modal ─────────────────────────────────────────────────────────

// function ReclassifyModal({
//   open,
//   draft,
//   onClose,
//   onConfirm,
//   submitting,
// }: {
//   open: boolean;
//   draft: ReplyDraft | null;
//   onClose: () => void;
//   onConfirm: (category: string, note: string) => void;
//   submitting: boolean;
// }) {
//   const [selectedCategory, setSelectedCategory] = useState('');
//   const [note, setNote] = useState('');

//   useEffect(() => {
//     if (!open) { setSelectedCategory(''); setNote(''); }
//   }, [open]);

//   if (!draft) return null;

//   const isDestructive = ['suppressed', 'action_required'].includes(selectedCategory);
//   const noteRequired = selectedCategory === 'action_required';
//   const canConfirm = !!selectedCategory && (!noteRequired || note.trim().length > 0) && !submitting;

//   return (
//     <Dialog open={open} onOpenChange={onClose}>
//       <DialogContent className="sm:max-w-md">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <Edit2 className="h-4 w-4" /> Override Category
//           </DialogTitle>
//           <DialogDescription>
//             Override the AI-assigned category for this reply.
//             The original AI classification is preserved for audit purposes.
//           </DialogDescription>
//         </DialogHeader>
//         <div className="space-y-4">
//           <div className="space-y-1.5">
//             <Label>New Category</Label>
//             <Select value={selectedCategory} onValueChange={setSelectedCategory}>
//               <SelectTrigger>
//                 <SelectValue placeholder="Select a category…" />
//               </SelectTrigger>
//               <SelectContent>
//                 <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
//                   Human Override
//                 </div>
//                 {RECLASSIFY_OPTIONS.filter((o) => o.group === 'human').map((o) => (
//                   <SelectItem key={o.value} value={o.value}>
//                     {o.label}
//                   </SelectItem>
//                 ))}
//                 <div className="px-2 py-1 mt-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
//                   Correct AI Category
//                 </div>
//                 {RECLASSIFY_OPTIONS.filter((o) => o.group === 'ai').map((o) => (
//                   <SelectItem key={o.value} value={o.value}>
//                     {o.label}
//                   </SelectItem>
//                 ))}
//               </SelectContent>
//             </Select>
//           </div>

//           {selectedCategory === 'action_required' && (
//             <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
//               <p className="font-semibold flex items-center gap-1.5">
//                 <TriangleAlert className="h-3 w-3" /> Action Required selected
//               </p>
//               <p>This marks the reply as a Sender Address Verification challenge. No prospect action will be taken. A note describing the required action is mandatory.</p>
//             </div>
//           )}

//           {selectedCategory === 'suppressed' && (
//             <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 space-y-1">
//               <p className="font-semibold flex items-center gap-1.5">
//                 <ShieldAlert className="h-3 w-3" /> Suppression selected
//               </p>
//               <p>This will immediately suppress the prospect from all future outreach. This action cannot be undone through the UI.</p>
//             </div>
//           )}

//           <div className="space-y-1.5">
//             <Label>
//               Note{noteRequired && <span className="text-destructive ml-1">*</span>}
//               {!noteRequired && <span className="text-muted-foreground text-xs ml-1">(optional)</span>}
//             </Label>
//             <Textarea
//               rows={3}
//               value={note}
//               onChange={(e) => setNote(e.target.value)}
//               placeholder={
//                 selectedCategory === 'action_required'
//                   ? 'Describe the action to take (e.g. "SAV challenge from Microsoft EOP — whitelist our IP")…'
//                   : 'Reason for override (e.g. "AI misread sarcasm", "Reply in French")…'
//               }
//             />
//           </div>
//         </div>
//         <DialogFooter>
//           <Button variant="outline" onClick={onClose}>Cancel</Button>
//           <Button
//             variant={isDestructive ? 'destructive' : 'default'}
//             onClick={() => onConfirm(selectedCategory, note)}
//             disabled={!canConfirm}
//           >
//             {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
//             {isDestructive ? 'Confirm Override' : 'Save Override'}
//           </Button>
//         </DialogFooter>
//       </DialogContent>
//     </Dialog>
//   );
// }

// // ─── GA4 UI visibility switch ─────────────────────────────────────────────────
// // Hidden until client GA4 access is confirmed. All GA4 code below stays in
// // place — set this to true to show the sync strip and the GA4 Engagement
// // column again. While false, no GA4 network calls are made from this page.
// const SHOW_GA4_UI = true;

// // ─── GA4 Engagement Cell ──────────────────────────────────────────────────────

// function EngagementCell({ sequenceId }: { sequenceId: string | null | undefined }) {
//   const { data: summaries, isLoading } = useSequenceEngagement(sequenceId);

//   if (isLoading) {
//     return <td className="p-3"><span className="inline-block w-16 h-4 rounded bg-muted/40 animate-pulse" /></td>;
//   }
//   if (!summaries || summaries.length === 0) {
//     return <td className="p-3 text-muted-foreground text-[11px]">No data</td>;
//   }

//   const engaged = isSequenceEngaged(summaries);
//   const seconds  = totalVisibleSeconds(summaries);

//   if (!engaged && seconds === 0) {
//     return <td className="p-3 text-muted-foreground text-[11px]">No data</td>;
//   }

//   return (
//     <td className="p-3">
//       <span
//         className={cn(
//           'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap',
//           engaged
//             ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400'
//             : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
//         )}
//         title={`${summaries.reduce((a, s) => a + s.totalSessions, 0)} session(s) recorded`}
//       >
//         {engaged ? (
//           <CheckCircle2 className="h-3 w-3" />
//         ) : (
//           <Activity className="h-3 w-3" />
//         )}
//         {engaged ? `Read · ${formatDwellTime(seconds)}` : `Opened · ${formatDwellTime(seconds)}`}
//       </span>
//     </td>
//   );
// }

// // ─── Component ────────────────────────────────────────────────────────────────

// export function ReplyInboxPage() {
//   const qc = useQueryClient();

//   const { data: rawReplies, isLoading } = useQuery({
//     queryKey: ['reply-drafts'],
//     queryFn: () => http.get<unknown>('/api/v1/reply-drafts'),
//     refetchInterval: 30_000,
//     retry: false,
//   });
//   const replies = normalise<ReplyDraft>(rawReplies);

//   const { data: autoPilotData } = useQuery({
//     queryKey: ['reply-drafts-autopilot'],
//     queryFn: () => http.get<AutoPilotPreview>('/api/v1/reply-drafts/auto-pilot'),
//     retry: false,
//   });

//   const { data: inboxStats, refetch: refetchStats } = useQuery<InboxStats>({
//     queryKey: ['reply-drafts-stats'],
//     queryFn: () => http.get<InboxStats>('/api/v1/reply-drafts/stats'),
//     refetchInterval: 30_000,
//     retry: false,
//   });

//   const { data: savAlertsData, refetch: refetchSavAlerts } = useQuery<SAVAlertsListResponse>({
//     queryKey: ['sav-alerts'],
//     queryFn: () => http.get<SAVAlertsListResponse>('/api/v1/reply-drafts/sav-alerts'),
//     refetchInterval: 60_000,
//     retry: false,
//   });

//   const { data: rawProspects } = useQuery({
//     queryKey: ['prospects-mini'],
//     queryFn: () => http.get<unknown>('/api/v1/prospects', { page: 1, page_size: 200 }),
//     staleTime: 60_000,
//   });
//   const prospects = normalise<Prospect>(rawProspects);

//   const [trackingDays, setTrackingDays] = useState(30);
//   const [showScanners, setShowScanners] = useState(false);

//   const { data: trackingSummary, isLoading: trackingLoading } = useQuery<TrackingSummary>({
//     queryKey: ['tracking-summary', trackingDays],
//     queryFn: () => http.get<TrackingSummary>('/api/v1/analytics/tracking-summary', { days: trackingDays }),
//     staleTime: 60_000,
//     retry: false,
//   });

//   const { data: rawBounced } = useQuery({
//     queryKey: ['sequences-bounced'],
//     queryFn: () => http.get<unknown>('/api/v1/sequences', { status: 'Bounced', limit: 200 }),
//     staleTime: 60_000,
//     retry: false,
//   });
//   const bouncedSequences = normalise<BouncedSequence>(rawBounced)
//     .sort((a, b) => {
//       const da = a.bouncedAt ? new Date(a.bouncedAt).getTime() : 0;
//       const db_ = b.bouncedAt ? new Date(b.bouncedAt).getTime() : 0;
//       return db_ - da; // descending — latest first
//     });

//   const { data: rawMessageBlocked } = useQuery({
//     queryKey: ['sequences-message-blocked'],
//     queryFn: () => http.get<unknown>('/api/v1/sequences', { status: 'MessageBlocked', limit: 200 }),
//     staleTime: 60_000,
//     retry: false,
//   });
//   const messageBlockedSequences = normalise<MessageBlockedSequence>(rawMessageBlocked)
//     .sort((a, b) => {
//       const da = a.messageBlockedAt ? new Date(a.messageBlockedAt).getTime() : 0;
//       const db_ = b.messageBlockedAt ? new Date(b.messageBlockedAt).getTime() : 0;
//       return db_ - da; // descending — latest first
//     });

//   const { data: rawClicked } = useQuery({
//     queryKey: ['click-tracking-clicked-sequences', showScanners],
//     queryFn: () => http.get<unknown>('/api/v1/click-tracking/clicked-sequences', {
//       limit: 200,
//       quality: showScanners ? 'all' : 'human',
//     }),
//     staleTime: 60_000,
//     retry: false,
//   });
//   const clickedSequences = normalise<ClickedSequence>(rawClicked);

//   // Deduplicate by (touchNumber, prospectEmail, originalUrl) — one row per unique combo, count = how many times clicked
//   const uniqueClickGroups = useMemo(() => {
//     const map = new Map<string, { data: ClickedSequence; count: number }>();
//     for (const c of clickedSequences) {
//       const key = `${c.touchNumber}|${c.prospectEmail ?? ''}|${c.originalUrl}`;
//       const existing = map.get(key);
//       if (existing) {
//         existing.count += 1;
//         // Keep the most recent clickedAt
//         if (new Date(c.clickedAt) > new Date(existing.data.clickedAt)) {
//           existing.data = { ...existing.data, clickedAt: c.clickedAt };
//         }
//       } else {
//         map.set(key, { data: c, count: 1 });
//       }
//     }
//     return Array.from(map.values());
//   }, [clickedSequences]);

//   const [selectedDraft, setSelectedDraft]         = useState<ReplyDraft | null>(null);
//   const [showAutoPilotOnly, setShowAutoPilotOnly] = useState(false);
//   const [categoryFilter, setCategoryFilter]       = useState<string>('all');
//   const [createDealOpen, setCreateDealOpen]       = useState(false);
//   const [dealTitle, setDealTitle]                 = useState('');
//   const [dealValue, setDealValue]                 = useState('');
//   const [dealNotes, setDealNotes]                 = useState('');
//   const [dealNextAction, setDealNextAction]       = useState('');
//   const [dealSuggesting, setDealSuggesting]       = useState(false);

//   const [logOpen, setLogOpen]             = useState(false);
//   const [logProspectId, setLogProspectId] = useState('');
//   const [logSequenceId, setLogSequenceId] = useState('');
//   const [logReplyText, setLogReplyText]   = useState('');
//   const [logSubmitting, setLogSubmitting] = useState(false);

//   // v4 reclassify state
//   const [reclassifyOpen, setReclassifyOpen]     = useState(false);
//   const [reclassifySubmitting, setReclassifySubmitting] = useState(false);

//   // v4 SAV alerts modal state
//   const [savAlertsOpen, setSavAlertsOpen]       = useState(false);
//   const [acknowledgingAlert, setAcknowledgingAlert] = useState<string | null>(null);

//   const { data: logSequencesRaw } = useQuery({
//     queryKey: ['sequences-for-log', logProspectId],
//     queryFn: () => http.get<unknown>('/api/v1/sequences', { prospect_id: logProspectId, limit: 50 }),
//     enabled: !!logProspectId,
//     staleTime: 30_000,
//   });
//   const logSequences = normalise<{ id: string; touchNumber: number; angle: string }>(logSequencesRaw);

//   const [apPreviewOpen, setApPreviewOpen]       = useState(false);
//   const [apPreviewLoading, setApPreviewLoading] = useState(false);
//   const [apPreview, setApPreview]               = useState<AutoPilotPreview | null>(null);
//   const [apConfirmOpen, setApConfirmOpen]       = useState(false);
//   const [apSending, setApSending]               = useState(false);
//   const [apResult, setApResult]                 = useState<AutoPilotSendResult | null>(null);
//   const [apResultOpen, setApResultOpen]         = useState(false);

//   const filteredDrafts = useMemo(() => {
//     let list = showAutoPilotOnly ? replies.filter((r) => r.autoPilotEligible) : replies;
//     if (categoryFilter !== 'all') {
//       list = list.filter((r) => {
//         const effectiveCat = getEffectiveCategory(r);
//         const normalised = LEGACY_CATEGORY_DISPLAY[effectiveCat] ?? effectiveCat;
//         return normalised === categoryFilter || effectiveCat === categoryFilter || r.category === categoryFilter;
//       });
//     }
//     return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
//   }, [replies, showAutoPilotOnly, categoryFilter]);

//   const autoPilotStats = useMemo(() => ({
//     eligible:      replies.filter((r) => r.autoPilotEligible && r.status === 'pending').length,
//     autoSent:      replies.filter((r) => r.status === 'auto_sent').length,
//     pendingReview: replies.filter((r) => r.status === 'pending').length,
//     total:         replies.length,
//   }), [replies]);

//   const pendingCount = replies.filter((r) => r.status === 'pending').length;

//   // ── GA4 Sync (inline) ────────────────────────────────────────────────────────
//   const [ga4SyncResult, setGa4SyncResult] = useState<{
//     rows_upserted: number; rows_skipped: number; errors: number; message: string;
//   } | null>(null);
//   const [ga4LastSync, setGa4LastSync]  = useState<Date | null>(null);

//   const { data: ga4Params } = useQuery({
//     queryKey: ['sys-params-ga4'],
//     queryFn: () => http.get<Array<{ key: string; value: string }>>('/api/v1/system-params'),
//     staleTime: 0,
//     refetchOnMount: true,
//     retry: false,
//     enabled: SHOW_GA4_UI,
//   });
//   const ga4Enabled = useMemo(() => {
//     const items = ga4Params ?? [];
//     return items.find((p) => p.key === 'ga4.enabled')?.value === 'true';
//   }, [ga4Params]);
//   const ga4HasPropertyId = useMemo(() => {
//     const items = ga4Params ?? [];
//     return (items.find((p) => p.key === 'ga4.property_id')?.value ?? '').trim().length > 0;
//   }, [ga4Params]);

//   const ga4SyncMut = useMutation({
//     mutationFn: () => http.post<{ rows_upserted: number; rows_skipped: number; errors: number; message: string }>(
//       '/api/v1/admin/ga4/sync', {}
//     ),
//     onSuccess: (result) => {
//       setGa4SyncResult(result);
//       setGa4LastSync(new Date());
//       if (result.errors > 0) {
//         toast.error(`GA4 sync completed with ${result.errors} error${result.errors > 1 ? 's' : ''}. Check server logs.`);
//       } else if (result.rows_upserted === 0 && result.rows_skipped === 0) {
//         toast.info('GA4 sync complete — no prospect link clicks found in GA4 yet.');
//       } else {
//         toast.success(`GA4 sync complete — ${result.rows_upserted} row${result.rows_upserted !== 1 ? 's' : ''} imported.`);
//       }
//       // Refresh engagement data for all cached sequences
//       qc.invalidateQueries({ queryKey: ['click-engagement'] });
//     },
//     onError: (err: unknown) => {
//       const detail =
//         err && typeof err === 'object' && 'response' in err
//           ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
//           : undefined;
//       toast.error(
//         typeof detail === 'string' && detail.trim().length > 0
//           ? `GA4 sync failed: ${detail}`
//           : 'GA4 sync failed. Check that credentials are saved and the service account has GA4 Viewer access.',
//       );
//     },
//   });

//   useEffect(() => {
//     if (!selectedDraft && filteredDrafts.length > 0) {
//       setSelectedDraft(filteredDrafts[0]);
//     }
//   }, [filteredDrafts, selectedDraft]);

//   useEffect(() => {
//     if (selectedDraft) {
//       const fresh = filteredDrafts.find((d) => d.id === selectedDraft.id);
//       if (fresh) setSelectedDraft(fresh);
//     }
//   }, [filteredDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

//   const approveMut = useMutation({
//     mutationFn: (id: string) => http.put(`/api/v1/reply-drafts/${id}`, { status: 'approved' }),
//     onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
//     onError: () => toast.error('Approve failed'),
//   });

//   const sendMut = useMutation({
//     mutationFn: (id: string) => http.post(`/api/v1/reply-drafts/${id}/auto-reply`, { dryRun: false }),
//     onSuccess: () => { toast.success('Reply sent!'); setSelectedDraft(null); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
//     onError: () => toast.error('Send failed'),
//   });

//   const dismissMut = useMutation({
//     mutationFn: (id: string) => http.put(`/api/v1/reply-drafts/${id}`, { status: 'dismissed' }),
//     onSuccess: () => { toast.success('Dismissed'); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
//     onError: () => toast.error('Dismiss failed'),
//   });

//   const createDealMut = useMutation({
//     mutationFn: (payload: { title: string; value: number; stage: string; notes?: string; prospectId?: string; source: string }) =>
//       http.post('/api/v1/deals', payload),
//     onSuccess: () => { toast.success('Deal created'); setCreateDealOpen(false); },
//     onError: () => toast.error('Failed to create deal'),
//   });

//   const categorizeMut = useMutation({
//     mutationFn: (draft: ReplyDraft) =>
//       http.post<CategorizeResult>(`/api/v1/reply-drafts/${draft.id}/reply-categorize`, {
//         originalReply: draft.originalReply,
//       }),
//     onSuccess: (result) => {
//       toast.success(`Categorized as: ${getCfg(result.category).label}`);
//       qc.invalidateQueries({ queryKey: ['reply-drafts'] });
//       qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
//     },
//     onError: () => toast.error('Categorization failed'),
//   });

//   const executeActionMut = useMutation({
//     mutationFn: (draftId: string) =>
//       http.post<ActionResult>(`/api/v1/reply-drafts/${draftId}/execute-action`, {}),
//     onSuccess: (_result, draftId) => {
//       const draft = replies.find((d) => d.id === draftId);
//       const cat = getEffectiveCategory(draft ?? ({} as ReplyDraft));
//       const msgs: Record<string, string> = {
//         hot_lead:        '🔥 Deal created and draft approved!',
//         warm_followup:   '✅ Approved — sequence continues',
//         curious:         '✅ Approved — stay in sequence',
//         ooo:             '⏰ Prospect snoozed to return date',
//         negative:        '🚫 Prospect suppressed from all campaigns',
//         bounce:          '⚡ Address handled',
//         wrong_company:   '🏢 Prospect marked as no longer there + suppressed',
//         unsubscribe:     '🚫 Consent withdrawn — permanently suppressed',
//         action_required: '⚠️ Flagged for operator action',
//         suppressed:      '🔇 Prospect suppressed',
//       };
//       toast.success(msgs[cat] ?? 'Action executed');
//       qc.invalidateQueries({ queryKey: ['reply-drafts'] });
//       qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
//     },
//     onError: () => toast.error('Action failed'),
//   });

//   // v4 — handle reclassify confirmation
//   const handleReclassifyConfirm = async (category: string, note: string) => {
//     if (!selectedDraft) return;
//     setReclassifySubmitting(true);
//     try {
//       const result = await http.post<ReclassifyResult>(
//         `/api/v1/reply-drafts/${selectedDraft.id}/reclassify`,
//         { category, note: note || null }
//       );
//       if (result.ok) {
//         const cfg = getCfg(category);
//         toast.success(`Override applied: ${cfg.label}`);
//         if (result.savAlertId) {
//           toast.info('⚠️ SAV alert created — check the Action Required banner.');
//         }
//         qc.invalidateQueries({ queryKey: ['reply-drafts'] });
//         qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
//         qc.invalidateQueries({ queryKey: ['sav-alerts'] });
//       } else {
//         toast.error(result.message || 'Override failed');
//       }
//     } catch {
//       toast.error('Override failed');
//     } finally {
//       setReclassifySubmitting(false);
//       setReclassifyOpen(false);
//     }
//   };

//   // v4 — acknowledge SAV alert
//   const handleAcknowledgeAlert = async (alertId: string, notes: string) => {
//     setAcknowledgingAlert(alertId);
//     try {
//       await http.patch(`/api/v1/reply-drafts/sav-alerts/${alertId}/acknowledge`, { notes: notes || null });
//       toast.success('Alert acknowledged');
//       refetchSavAlerts();
//       refetchStats();
//     } catch {
//       toast.error('Acknowledge failed');
//     } finally {
//       setAcknowledgingAlert(null);
//     }
//   };

//   // const handleExportCsv = () => {
//   //   const headers = ['id', 'category', 'manualCategory', 'isHumanReviewed', 'status', 'confidence', 'autoPilotEligible', 'prospectName', 'prospectEmail', 'sentEmailSubject', 'originalReply', 'draftBody', 'createdAt'];
//   //   const csv = [
//   //     headers.join(','),
//   //     ...replies.map((r) =>
//   //       headers.map((h) => {
//   //         const val = String((r as unknown as Record<string, unknown>)[h] ?? '');
//   //         return val.includes(',') ? `"${val.replace(/"/g, '""')}"` : val;
//   //       }).join(',')
//   //     ),
//   //   ].join('\n');
//   //   const blob = new Blob([csv], { type: 'text/csv' });
//   //   const url = URL.createObjectURL(blob);
//   //   const a = document.createElement('a');
//   //   a.href = url; a.download = `reply-drafts-${new Date().toISOString().split('T')[0]}.csv`; a.click();
//   //   URL.revokeObjectURL(url);
//   //   toast.success('CSV exported');
//   // };

//   const handleLogReply = async () => {
//     if (!logReplyText.trim() || !logProspectId || !logSequenceId) { toast.error('Select a prospect, sequence, and enter reply text'); return; }
//     setLogSubmitting(true);
//     try {
//       await http.post('/api/v1/reply-drafts', { prospectId: logProspectId, sequenceId: logSequenceId, originalReply: logReplyText, category: 'other' });
//       toast.success('Reply logged');
//       setLogOpen(false); setLogProspectId(''); setLogSequenceId(''); setLogReplyText('');
//       qc.invalidateQueries({ queryKey: ['reply-drafts'] });
//     } catch { toast.error('Failed to log reply'); }
//     finally { setLogSubmitting(false); }
//   };

//   const handlePreviewAutoPilot = async () => {
//     setApPreviewLoading(true); setApPreviewOpen(true);
//     try {
//       const result = await http.get<AutoPilotPreview>('/api/v1/reply-drafts/auto-pilot');
//       setApPreview(result);
//     } catch { toast.error('Failed to load preview'); setApPreview({ eligible: [], count: 0 }); }
//     finally { setApPreviewLoading(false); }
//   };

//   const handleRunAutoPilot = async () => {
//     setApConfirmOpen(false); setApSending(true);
//     try {
//       const eligible = apPreview?.eligible ?? autoPilotData?.eligible ?? [];
//       const results = await Promise.allSettled(
//         eligible.map((d) => http.post(`/api/v1/reply-drafts/${d.id}/auto-reply`, { dryRun: false }))
//       );
//       const sent = results.filter((r) => r.status === 'fulfilled').length;
//       const failed = results.filter((r) => r.status === 'rejected').length;
//       setApResult({ sent, failed, markedOnly: 0, total: results.length });
//       setApResultOpen(true);
//       qc.invalidateQueries({ queryKey: ['reply-drafts'] });
//       qc.invalidateQueries({ queryKey: ['reply-drafts-autopilot'] });
//       if (sent > 0) toast.success(`Auto-Pilot sent ${sent} draft${sent === 1 ? '' : 's'}`);
//       if (failed > 0) toast.error(`${failed} draft${failed === 1 ? '' : 's'} failed`);
//     } catch { toast.error('Auto-Pilot run failed'); }
//     finally { setApSending(false); }
//   };

//   const openCreateDeal = async (draft: ReplyDraft) => {
//     setDealTitle(''); setDealValue(''); setDealNotes(''); setDealNextAction('');
//     setCreateDealOpen(true);
//     setDealSuggesting(true);
//     try {
//       const result = await http.post<{ suggestion?: string; nextAction?: string; dealTitle?: string; dealValue?: number; dealNotes?: string }>(
//         `/api/v1/deals/temp/deal-suggest`, { replyDraftId: draft.id }
//       );
//       setDealTitle(result.dealTitle ?? `${draft.category.replace(/_/g, ' ')} — Deal`);
//       setDealValue(String(result.dealValue ?? ''));
//       setDealNotes(result.dealNotes ?? result.suggestion ?? '');
//       setDealNextAction(result.nextAction ?? '');
//     } catch { /* suggestion failed silently */ }
//     finally { setDealSuggesting(false); }
//   };

//   const selectedExtra = useMemo(
//     () => parseExtra(selectedDraft?.extraData ?? null),
//     [selectedDraft]
//   );

//   const selectedEffectiveCat = selectedDraft ? getEffectiveCategory(selectedDraft) : '';
//   const selectedCfg = selectedDraft ? getCfg(selectedEffectiveCat) : null;
//   const unacknowledgedSavAlerts = inboxStats?.unacknowledgedSavAlerts ?? 0;
//   const savAlertsList = savAlertsData?.alerts ?? [];

//   return (
//     <div className="space-y-4">

//       {/* ── SAV Alert Banner ──────────────────────────────────────────── */}
//       <SAVAlertBanner
//         count={unacknowledgedSavAlerts}
//         onViewAlerts={() => setSavAlertsOpen(true)}
//       />

//       {/* ── GA4 Click Engagement Sync Strip (hidden while SHOW_GA4_UI = false) ── */}
//       {SHOW_GA4_UI && (
//       <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 px-4 py-2.5 flex-wrap">
//         <div className="flex items-center gap-2.5 min-w-0">
//           <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
//           <span className="text-xs font-medium text-blue-900 dark:text-blue-200">GA4 Click Engagement</span>
//           {ga4Enabled ? (
//             <span className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400">
//               <Wifi className="h-2.5 w-2.5" /> Enabled
//             </span>
//           ) : (
//             <span className="inline-flex items-center gap-1 rounded-full border border-muted-foreground/30 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
//               <WifiOff className="h-2.5 w-2.5" /> Disabled
//             </span>
//           )}
//           {ga4LastSync && (
//             <span className="text-[11px] text-muted-foreground hidden sm:inline">
//               · Last synced {ga4LastSync.toLocaleTimeString()}
//               {ga4SyncResult && ga4SyncResult.rows_upserted > 0 && (
//                 <span className="ml-1 text-green-700 font-medium">({ga4SyncResult.rows_upserted} rows)</span>
//               )}
//             </span>
//           )}
//           {!ga4Enabled && ga4Params && (
//             <span className="text-[11px] text-amber-700 hidden md:inline">
//               · Configure ga4.* params in System Parameters to enable
//             </span>
//           )}
//         </div>
//         <Button
//           size="sm"
//           variant="outline"
//           className="h-7 text-xs shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
//           onClick={() => ga4SyncMut.mutate()}
//           disabled={ga4SyncMut.isPending || !ga4Enabled || !ga4HasPropertyId}
//           title={!ga4Enabled ? 'Enable GA4 in System Parameters first' : !ga4HasPropertyId ? 'Set ga4.property_id in System Parameters first' : 'Pull latest GA4 engagement data now'}
//         >
//           {ga4SyncMut.isPending ? (
//             <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Syncing…</>
//           ) : (
//             <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Sync GA4 Now</>
//           )}
//         </Button>
//       </div>
//       )}

//       {/* ── Tracking Dashboard Strip ──────────────────────────────────── */}
//       <div className="flex items-center justify-between">
//         <h2 className="text-sm font-semibold text-muted-foreground">Email Activity Overview</h2>
//         <Select value={String(trackingDays)} onValueChange={(v) => setTrackingDays(Number(v))}>
//           <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
//           <SelectContent>
//             {[7, 14, 30, 60, 90].map(d => (
//               <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>
//             ))}
//           </SelectContent>
//         </Select>
//       </div>

//       {trackingLoading ? (
//         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//           {[0,1,2,3].map(i => <div key={i} className="rounded-lg border p-4 animate-pulse bg-muted/30 h-20" />)}
//         </div>
//       ) : (
//         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//           {/* Sent */}
//           <Card className="border bg-blue-50/50 border-blue-200">
//             <CardContent className="p-3">
//               <div className="flex items-center gap-1.5 mb-1 text-blue-700">
//                 <Mail className="h-3.5 w-3.5 text-blue-600" />
//                 <span className="text-xs font-medium">Sent</span>
//               </div>
//               <p className="text-xl font-bold text-blue-700">{(trackingSummary?.total_sent ?? 0).toLocaleString()}</p>
//               <p className="text-[11px] text-muted-foreground mt-0.5">Last {trackingDays} days</p>
//             </CardContent>
//           </Card>

//           {/* Replied */}
//           <Card className="border bg-emerald-50/50 border-emerald-200">
//             <CardContent className="p-3">
//               <div className="flex items-center gap-1.5 mb-1 text-emerald-700">
//                 <MessageCircleReply className="h-3.5 w-3.5 text-emerald-600" />
//                 <span className="text-xs font-medium">Replied</span>
//               </div>
//               <p className="text-xl font-bold text-emerald-700">{(trackingSummary?.total_replied ?? 0).toLocaleString()}</p>
//               <p className="text-[11px] text-muted-foreground mt-0.5">{trackingSummary ? `${(trackingSummary.reply_rate * 100).toFixed(1)}% rate` : '—'}</p>
//             </CardContent>
//           </Card>

//           {/* Bounced — rate + reasons on one line, benchmark badge in header */}
//           {(() => {
//             const bounceRate = trackingSummary?.bounce_rate ?? 0;
//             const totalBounced = trackingSummary?.total_bounced ?? 0;
//             const isHigh = bounceRate > 0.05;
//             const reasons = trackingSummary?.top_bounce_reasons ?? [];
//             return (
//               <Card className={cn(
//                 'border col-span-2 md:col-span-1',
//                 isHigh ? 'bg-red-50/50 border-red-200' : 'bg-orange-50/50 border-orange-200'
//               )}>
//                 <CardContent className="p-3">
//                   <div className={cn('flex items-center gap-1.5 mb-1', isHigh ? 'text-red-700' : 'text-orange-700')}>
//                     <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
//                     <span className="text-xs font-medium">Bounced</span>
//                     {isHigh && (
//                       <span className="ml-auto text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-0.5 shrink-0">
//                         &gt;5% benchmark
//                       </span>
//                     )}
//                   </div>
//                   <p className={cn('text-xl font-bold', isHigh ? 'text-red-700' : 'text-orange-700')}>
//                     {totalBounced.toLocaleString()}
//                   </p>
//                   <div className="flex items-center flex-wrap gap-1 mt-0.5">
//                     <span className="text-[11px] text-muted-foreground shrink-0">
//                       {trackingSummary ? `${(bounceRate * 100).toFixed(1)}% rate` : '—'}
//                     </span>
//                     {reasons.map(r => (
//                       <span key={r.reason} className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-orange-100 text-orange-800 text-[10px] border border-orange-200 leading-4">
//                         <span className="font-semibold">{r.count}×</span>
//                         <span className="truncate max-w-[70px]">{r.reason}</span>
//                       </span>
//                     ))}
//                   </div>
//                 </CardContent>
//               </Card>
//             );
//           })()}

//           {/* Link Clicked */}
//           <Card className="border bg-violet-50/50 border-violet-200">
//             <CardContent className="p-3">
//               <div className="flex items-center gap-1.5 mb-1 text-violet-700">
//                 <MousePointerClick className="h-3.5 w-3.5 text-violet-600" />
//                 <span className="text-xs font-medium">Link Clicked</span>
//               </div>
//               <p className="text-xl font-bold text-violet-700">{uniqueClickGroups.length.toLocaleString()}</p>
//               <p className="text-[11px] text-muted-foreground mt-0.5">Unique links clicked</p>
//             </CardContent>
//           </Card>
//         </div>
//       )}

//       <Tabs defaultValue="manual" className="w-full">
//         <TabsList className="grid w-full max-w-2xl grid-cols-5">
//           <TabsTrigger value="manual">
//             <MessageCircleReply className="h-3.5 w-3.5 mr-1.5" /> Manual Review
//           </TabsTrigger>
//           <TabsTrigger value="autopilot">
//             <Bot className="h-3.5 w-3.5 mr-1.5" /> Auto-Pilot
//             {autoPilotStats.eligible > 0 && (
//               <Badge className="ml-1.5 bg-violet-100 text-violet-700 border-violet-200 text-[10px] px-1 py-0 border">
//                 {autoPilotStats.eligible}
//               </Badge>
//             )}
//           </TabsTrigger>
//           <TabsTrigger value="bounced">
//             <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Bounced
//             {bouncedSequences.length > 0 && (
//               <Badge className="ml-1.5 bg-red-100 text-red-700 border-red-200 text-[10px] px-1 py-0 border">
//                 {bouncedSequences.length}
//               </Badge>
//             )}
//           </TabsTrigger>
//           <TabsTrigger value="message-blocked">
//             <ShieldX className="h-3.5 w-3.5 mr-1.5" /> Message Blocked
//             {messageBlockedSequences.length > 0 && (
//               <Badge className="ml-1.5 bg-orange-100 text-orange-700 border-orange-200 text-[10px] px-1 py-0 border">
//                 {messageBlockedSequences.length}
//               </Badge>
//             )}
//           </TabsTrigger>
//           <TabsTrigger value="clicked">
//             <MousePointerClick className="h-3.5 w-3.5 mr-1.5" /> Link Clicked
//             {uniqueClickGroups.length > 0 && (
//               <Badge className="ml-1.5 bg-violet-100 text-violet-700 border-violet-200 text-[10px] px-1 py-0 border">
//                 {uniqueClickGroups.length}
//               </Badge>
//             )}
//           </TabsTrigger>
//         </TabsList>

//         {/* ── Manual Review Tab ─────────────────────────────────────────── */}
//         <TabsContent value="manual" className="space-y-4 mt-4">

//           {/* Positive reply rate stat strip */}
//           {inboxStats && inboxStats.total > 0 && (
//             <div className="flex flex-wrap gap-3 items-center p-3 rounded-lg border bg-muted/20">
//               <div className="text-xs text-muted-foreground">
//                 <span className="font-semibold text-foreground">{inboxStats.total}</span> Total Replies
//               </div>
//               <div className={cn('text-xs font-medium', inboxStats.positiveReplyRate >= 0.05 ? 'text-emerald-700' : 'text-amber-700')}>
//                 {Math.round(inboxStats.positiveReplyRate * 100)}% Positive Rate
//                 <span className="ml-1 font-normal text-muted-foreground">
//                   ({inboxStats.hotLeads} Hot + {inboxStats.warmFollowups} Warm)
//                 </span>
//               </div>
//               {inboxStats.wrongCompany > 0 && (
//                 <div className="text-xs text-purple-700 font-medium">
//                   🏢 {inboxStats.wrongCompany} Wrong Company
//                 </div>
//               )}
//               {inboxStats.unsubscribe > 0 && (
//                 <div className="text-xs text-rose-700 font-medium">
//                   🚫 {inboxStats.unsubscribe} Unsubscribed
//                 </div>
//               )}
//               {/* v4: human-category counts */}
//               {(inboxStats.actionRequired ?? 0) > 0 && (
//                 <div className="text-xs text-amber-800 font-medium cursor-pointer hover:underline" onClick={() => setSavAlertsOpen(true)}>
//                   ⚠️ {inboxStats.actionRequired} Action Required
//                 </div>
//               )}
//               {(inboxStats.suppressed ?? 0) > 0 && (
//                 <div className="text-xs text-red-800 font-medium">
//                   🔇 {inboxStats.suppressed} Suppressed
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Category filter pills */}
//           <div className="flex flex-wrap gap-1.5">
//             {FILTER_PILLS.map(({ key, label }) => {
//               const count = key === 'all'
//                 ? replies.length
//                 : replies.filter((r) => {
//                     const effectiveCat = getEffectiveCategory(r);
//                     const n = LEGACY_CATEGORY_DISPLAY[effectiveCat] ?? effectiveCat;
//                     return n === key || effectiveCat === key || r.category === key;
//                   }).length;
//               return (
//                 <button
//                   key={key}
//                   onClick={() => { setCategoryFilter(key); setSelectedDraft(null); }}
//                   className={cn(
//                     'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
//                     categoryFilter === key
//                       ? 'bg-primary text-primary-foreground border-primary'
//                       : 'bg-background border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'
//                   )}
//                 >
//                   {label}
//                   <span className="rounded-full bg-black/10 dark:bg-white/10 px-1 text-[10px]">{count}</span>
//                 </button>
//               );
//             })}
//           </div>

//           <div className="flex items-center gap-3 flex-wrap">
//             {pendingCount > 0 && <Badge variant="default" className="text-xs">{pendingCount} pending</Badge>}
//             <span className="text-xs text-muted-foreground">Auto-refreshes every 30s</span>
//             {/* <Button variant="outline" size="sm" onClick={() => setLogOpen(true)}>
//               <MessageCircleReply className="h-4 w-4 mr-1" /> Log a reply
//             </Button> */}
//             {/* <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={replies.length === 0}>
//               <FileDown className="h-4 w-4 mr-1" /> Export CSV
//             </Button> */}
//             <div className="ml-auto flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
//               <Switch id="filter-ap" checked={showAutoPilotOnly} onCheckedChange={setShowAutoPilotOnly} />
//               <Label htmlFor="filter-ap" className="text-xs font-medium cursor-pointer flex items-center gap-1">
//                 <Zap className="h-3 w-3 text-violet-600" /> Show Only Auto-Pilot Eligible Drafts
//               </Label>
//             </div>
//           </div>

//           <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
//             {/* ── Left: Draft List ─────────────────────────────────────── */}
//             <Card className="lg:col-span-2">
//               <CardHeader className="pb-2">
//                 <CardTitle className="text-sm flex items-center justify-between">
//                   <span>
//                     Reply Drafts
//                     {showAutoPilotOnly && (
//                       <Badge variant="outline" className="ml-2 text-[10px]">Filter: Auto-Pilot Eligible</Badge>
//                     )}
//                   </span>
//                   <span className="text-xs text-muted-foreground font-normal">{filteredDrafts.length} shown</span>
//                 </CardTitle>
//               </CardHeader>
//               <CardContent className="p-0">
//                 <div className="max-h-[640px] overflow-y-auto">
//                   {isLoading ? (
//                     <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
//                   ) : filteredDrafts.length === 0 ? (
//                     <div className="p-6 text-center text-sm text-muted-foreground">
//                       {showAutoPilotOnly
//                         ? 'No Auto-Pilot Eligible Drafts.'
//                         : categoryFilter !== 'all'
//                         ? 'No replies in this category.'
//                         : 'No reply drafts yet. When prospects reply, they appear here automatically.'
//                       }
//                     </div>
//                   ) : filteredDrafts.map((d) => {
//                     const effectiveCat = getEffectiveCategory(d);
//                     return (
//                       <button
//                         key={d.id}
//                         onClick={() => setSelectedDraft(d)}
//                         className={cn(
//                           'w-full text-left p-3 border-b last:border-0 hover:bg-muted/30 transition-colors',
//                           selectedDraft?.id === d.id && 'bg-muted/50',
//                           // v4: left border colour by effective category
//                           effectiveCat === 'action_required'
//                             ? 'border-l-2 border-l-amber-400'
//                             : effectiveCat === 'suppressed'
//                             ? 'border-l-2 border-l-red-400'
//                             : d.isHumanReviewed
//                             ? 'border-l-2 border-l-blue-400'
//                             : selectedDraft?.id === d.id
//                             ? 'border-l-2 border-l-primary'
//                             : ''
//                         )}
//                       >
//                         {/* Prospect name + status badge */}
//                         <div className="flex items-center justify-between gap-2 mb-1">
//                           <span className="text-xs font-semibold truncate">
//                             {d.prospectName ?? d.prospectEmail ?? 'Unknown Prospect'}
//                           </span>
//                           <span className={cn('px-1.5 py-0.5 rounded text-[10px] shrink-0', STATUS_COLORS[d.status] ?? 'bg-gray-100')}>
//                             {DRAFT_STATUS_LABEL[d.status] ?? d.status}
//                           </span>
//                         </div>

//                         {/* Category badge (uses CategoryBadge with tooltip for human-reviewed) */}
//                         <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
//                           <CategoryBadge draft={d} size="xs" />
//                           {d.autoPilotEligible && (
//                             <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 flex items-center gap-0.5">
//                               <Zap className="h-2.5 w-2.5" /> auto-pilot
//                             </span>
//                           )}
//                         </div>

//                         {d.sentEmailSubject && (
//                           <p className="text-[10px] text-muted-foreground mb-0.5 truncate">
//                             📧 {d.sentEmailSubject}
//                           </p>
//                         )}

//                         <p className="text-xs text-foreground/80 line-clamp-2 leading-snug">{d.originalReply}</p>
//                         <p className="text-[10px] text-muted-foreground mt-1">{new Date(d.createdAt).toLocaleString()}</p>
//                       </button>
//                     );
//                   })}
//                 </div>
//               </CardContent>
//             </Card>

//             {/* ── Right: Conversation Thread + Actions ─────────────────── */}
//             <Card className="lg:col-span-3">
//               {!selectedDraft ? (
//                 <CardContent className="flex items-center justify-center h-full min-h-[200px]">
//                   <p className="text-sm text-muted-foreground">Select a reply to view the conversation thread.</p>
//                 </CardContent>
//               ) : (
//                 <>
//                   <CardHeader className="pb-3 border-b">
//                     <div className="flex items-start justify-between gap-2 flex-wrap">
//                       <div>
//                         <CardTitle className="text-base">
//                           {selectedDraft.prospectName ?? selectedDraft.prospectEmail ?? 'Unknown Prospect'}
//                         </CardTitle>
//                         {selectedDraft.prospectEmail && (
//                           <p className="text-xs text-muted-foreground mt-0.5">{selectedDraft.prospectEmail}</p>
//                         )}
//                       </div>
//                       <div className="flex items-center gap-1.5 flex-wrap">
//                         {/* Category badge with tooltip */}
//                         <CategoryBadge draft={selectedDraft} size="sm" />
//                         <span className={cn('px-2 py-0.5 rounded text-xs', STATUS_COLORS[selectedDraft.status] ?? 'bg-gray-100')}>
//                           {DRAFT_STATUS_LABEL[selectedDraft.status] ?? selectedDraft.status}
//                         </span>
//                         {selectedDraft.autoPilotEligible && (
//                           <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 flex items-center gap-1">
//                             <Zap className="h-3 w-3" /> Auto-Pilot Eligible
//                           </span>
//                         )}
//                         {selectedDraft.confidence != null && (
//                           <span className="text-xs text-muted-foreground">
//                             {Math.round(selectedDraft.confidence * 100)}% confidence
//                           </span>
//                         )}
//                         {/* Re-classify AI button */}
//                         <Button
//                           variant="ghost"
//                           size="sm"
//                           className="h-6 px-2 text-xs"
//                           onClick={() => categorizeMut.mutate(selectedDraft)}
//                           disabled={categorizeMut.isPending || !!selectedDraft.manualCategory}
//                           title={selectedDraft.manualCategory ? 'Category is human-locked — clear the override to re-classify' : 'Re-run AI categorization'}
//                         >
//                           <RefreshCw className={cn('h-3 w-3 mr-1', categorizeMut.isPending && 'animate-spin')} />
//                           Re-classify
//                         </Button>
//                         {/* v4: Override Category button */}
//                         <Button
//                           variant="outline"
//                           size="sm"
//                           className="h-6 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
//                           onClick={() => setReclassifyOpen(true)}
//                         >
//                           <Edit2 className="h-3 w-3 mr-1" />
//                           Override
//                         </Button>
//                       </div>
//                     </div>
//                     {selectedDraft.summary && (
//                       <p className="text-xs text-muted-foreground mt-1 italic">{selectedDraft.summary}</p>
//                     )}
//                     {/* v4: show human override note if set */}
//                     {selectedDraft.manualCategoryNote && (
//                       <div className="mt-1.5 rounded border border-blue-200 bg-blue-50 px-2 py-1.5 flex items-start gap-1.5">
//                         <Edit2 className="h-3 w-3 text-blue-600 shrink-0 mt-0.5" />
//                         <p className="text-xs text-blue-800">
//                           <span className="font-semibold">Override note:</span> {selectedDraft.manualCategoryNote}
//                         </p>
//                       </div>
//                     )}
//                   </CardHeader>

//                   <CardContent className="pt-4">
//                     <div className="space-y-4">

//                       {/* action_required: SAV panel */}
//                       {selectedEffectiveCat === 'action_required' && (
//                         <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
//                           <div className="flex items-center gap-2">
//                             <TriangleAlert className="h-4 w-4 text-amber-700" />
//                             <p className="text-sm font-semibold text-amber-800">
//                               Sender Address Verification — Operator Action Required
//                             </p>
//                           </div>
//                           <p className="text-xs text-amber-700">
//                             This email is an infrastructure challenge from a mail server, not a prospect reply.
//                             No prospect action has been taken. Review the alert and acknowledge it once resolved.
//                           </p>
//                           <Button
//                             size="sm"
//                             variant="outline"
//                             className="border-amber-400 text-amber-800 hover:bg-amber-100"
//                             onClick={() => setSavAlertsOpen(true)}
//                           >
//                             <TriangleAlert className="h-3.5 w-3.5 mr-1" /> View Alerts
//                           </Button>
//                         </div>
//                       )}

//                       {/* AI suggested action panel */}
//                       {selectedDraft.suggestedAction && selectedCfg && selectedEffectiveCat !== 'action_required' && (
//                         <div className={cn('rounded-lg border p-3 flex items-start gap-2', selectedCfg.bgColor, selectedCfg.borderColor)}>
//                           <Sparkles className={cn('h-4 w-4 shrink-0 mt-0.5', selectedCfg.textColor)} />
//                           <div>
//                             <p className={cn('text-xs font-semibold mb-0.5', selectedCfg.textColor)}>AI Recommended Action</p>
//                             <p className="text-sm">{selectedDraft.suggestedAction}</p>
//                           </div>
//                         </div>
//                       )}

//                       {/* wrong_company special panel */}
//                       {selectedEffectiveCat === 'wrong_company' && (
//                         <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-2">
//                           <div className="flex items-center gap-2">
//                             <Building2 className="h-4 w-4 text-purple-700" />
//                             <p className="text-sm font-semibold text-purple-700">Prospect no longer works there</p>
//                           </div>
//                           {selectedExtra.newCompany && (
//                             <p className="text-sm"><span className="font-medium">Now at:</span> {selectedExtra.newCompany}</p>
//                           )}
//                           {selectedExtra.referralName && (
//                             <p className="text-sm"><span className="font-medium">Referred to:</span> {selectedExtra.referralName}</p>
//                           )}
//                           <div className="flex gap-2 flex-wrap">
//                             <Button
//                               size="sm"
//                               variant="outline"
//                               className="border-purple-300 text-purple-700 hover:bg-purple-100"
//                               onClick={() => toast.info('Search for replacement via LinkedIn Hub or Datamoon')}
//                             >
//                               <Search className="h-3.5 w-3.5 mr-1" /> Find Replacement
//                             </Button>
//                           </div>
//                         </div>
//                       )}

//                       {/* OOO snooze info panel */}
//                       {selectedEffectiveCat === 'ooo' && (
//                         <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 flex items-center gap-2">
//                           <Clock className="h-4 w-4 text-sky-600 shrink-0" />
//                           <div>
//                             <p className="text-sm font-medium text-sky-800">
//                               {selectedExtra.returnDate
//                                 ? `Return date: ${new Date(selectedExtra.returnDate).toLocaleDateString()}`
//                                 : 'Return date unknown — defaulting to 2 weeks'}
//                             </p>
//                             {selectedDraft.snoozedUntil && (
//                               <p className="text-xs text-muted-foreground mt-0.5">
//                                 Sequence will resume {new Date(selectedDraft.snoozedUntil).toLocaleDateString()}
//                               </p>
//                             )}
//                           </div>
//                         </div>
//                       )}

//                       <ConversationThread draft={selectedDraft} />

//                       {/* Action Buttons */}
//                       <div className="flex gap-2 flex-wrap pt-2 border-t">
//                         {selectedDraft.status === 'pending' && (
//                           <>
//                             <Button size="sm" onClick={() => approveMut.mutate(selectedDraft.id)} disabled={approveMut.isPending}>
//                               <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
//                             </Button>
//                             <Button size="sm" variant="outline" onClick={() => sendMut.mutate(selectedDraft.id)} disabled={sendMut.isPending}>
//                               <Send className="h-4 w-4 mr-1" />{sendMut.isPending ? 'Sending…' : 'Send Now'}
//                             </Button>

//                             {selectedCfg && selectedEffectiveCat !== 'suppressed' && (
//                               <Button
//                                 size="sm"
//                                 variant={selectedCfg.actionVariant}
//                                 onClick={() => executeActionMut.mutate(selectedDraft.id)}
//                                 disabled={executeActionMut.isPending}
//                                 className={cn(
//                                   selectedEffectiveCat === 'wrong_company' && 'border-purple-300 text-purple-700 hover:bg-purple-50',
//                                   selectedEffectiveCat === 'ooo' && 'border-sky-300 text-sky-700 hover:bg-sky-50',
//                                   selectedEffectiveCat === 'action_required' && 'border-amber-400 text-amber-800 hover:bg-amber-50',
//                                 )}
//                               >
//                                 {executeActionMut.isPending
//                                   ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
//                                   : selectedCfg.icon
//                                 }
//                                 {selectedCfg.actionLabel}
//                               </Button>
//                             )}

//                             <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
//                               onClick={() => dismissMut.mutate(selectedDraft.id)} disabled={dismissMut.isPending}>
//                               <Ban className="h-4 w-4 mr-1" /> Dismiss
//                             </Button>
//                           </>
//                         )}
//                         {selectedDraft.status === 'approved' && (
//                           <Button size="sm" onClick={() => sendMut.mutate(selectedDraft.id)} disabled={sendMut.isPending}>
//                             <Send className="h-4 w-4 mr-1" />{sendMut.isPending ? 'Sending…' : 'Send Now'}
//                           </Button>
//                         )}
//                         {(selectedDraft.status === 'sent' || selectedDraft.status === 'auto_sent') && (
//                           <Badge className="bg-emerald-100 text-emerald-700">
//                             <CheckCircle2 className="h-3 w-3 mr-1" />
//                             {selectedDraft.status === 'auto_sent' ? 'Auto-Sent' : 'Sent'}
//                           </Badge>
//                         )}
//                         {selectedDraft.status === 'handled' && (
//                           <Badge className="bg-emerald-100 text-emerald-700">
//                             <CheckCircle2 className="h-3 w-3 mr-1" /> Handled
//                           </Badge>
//                         )}
//                         {POSITIVE_CATEGORIES.includes(selectedEffectiveCat) && (
//                           <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
//                             onClick={() => openCreateDeal(selectedDraft)}>
//                             <DollarSign className="h-4 w-4 mr-1" />
//                             {dealSuggesting && createDealOpen ? 'Analyzing…' : 'Create Deal'}
//                           </Button>
//                         )}
//                       </div>

//                       {/* Playbook sidebar hint */}
//                       {selectedCfg && selectedCfg.playbook.length > 0 && (
//                         <div className={cn('rounded-lg border p-3 text-xs space-y-1.5', selectedCfg.bgColor, selectedCfg.borderColor)}>
//                           <p className={cn('font-semibold', selectedCfg.textColor)}>Playbook — {selectedCfg.label}</p>
//                           <ul className="space-y-0.5 text-muted-foreground">
//                             {selectedCfg.playbook.map((step, i) => (
//                               <li key={i}>• {step}</li>
//                             ))}
//                           </ul>
//                           {selectedEffectiveCat === 'unsubscribe' && (
//                             <p className="text-rose-700 font-medium flex items-center gap-1">
//                               <AlertTriangle className="h-3 w-3" /> This action is permanent and irrevocable.
//                             </p>
//                           )}
//                         </div>
//                       )}

//                       {/* Inline deal form */}
//                       {createDealOpen && (
//                         <Card className="border-emerald-200 bg-emerald-50/50">
//                           <CardContent className="p-3 space-y-2">
//                             <p className="text-xs font-medium flex items-center gap-1">
//                               <Sparkles className="h-3 w-3" /> AI-Suggested Deal
//                             </p>
//                             <div className="grid grid-cols-2 gap-2">
//                               <div>
//                                 <Label className="text-xs">Deal Title</Label>
//                                 <Input className="h-8 text-sm mt-1" value={dealTitle} onChange={(e) => setDealTitle(e.target.value)} />
//                               </div>
//                               <div>
//                                 <Label className="text-xs">Value ($)</Label>
//                                 <Input className="h-8 text-sm mt-1" type="number" placeholder="0" value={dealValue} onChange={(e) => setDealValue(e.target.value)} />
//                               </div>
//                             </div>
//                             {dealNotes && (
//                               <div>
//                                 <Label className="text-xs">AI Notes</Label>
//                                 <p className="text-xs text-muted-foreground mt-1 bg-white rounded p-2 border">{dealNotes}</p>
//                               </div>
//                             )}
//                             {dealNextAction && (
//                               <div>
//                                 <Label className="text-xs">Suggested Next Action</Label>
//                                 <p className="text-xs text-emerald-800 mt-1 bg-white rounded p-2 border border-emerald-200">{dealNextAction}</p>
//                               </div>
//                             )}
//                             <div className="flex gap-2">
//                               <Button size="sm" disabled={!dealTitle || createDealMut.isPending}
//                                 onClick={() => createDealMut.mutate({ title: dealTitle, value: parseFloat(dealValue) || 0, stage: 'qualified', notes: dealNotes || undefined, prospectId: selectedDraft?.prospectId, source: 'cold_email' })}>
//                                 {createDealMut.isPending ? 'Creating…' : 'Create Deal'}
//                               </Button>
//                               <Button size="sm" variant="ghost" onClick={() => setCreateDealOpen(false)}>Cancel</Button>
//                             </div>
//                           </CardContent>
//                         </Card>
//                       )}
//                     </div>
//                   </CardContent>
//                 </>
//               )}
//             </Card>
//           </div>
//         </TabsContent>

//         {/* ── Auto-Pilot Tab ────────────────────────────────────────────── */}
//         <TabsContent value="autopilot" className="space-y-4 mt-4">
//           <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//             <Card className="border-violet-200 bg-violet-50/30">
//               <CardContent className="p-4">
//                 <div className="flex items-center gap-2 text-violet-700"><Zap className="h-4 w-4" /><span className="text-xs font-medium">Eligible</span></div>
//                 <p className="text-2xl font-bold mt-1">{autoPilotStats.eligible}</p>
//                 <p className="text-[11px] text-muted-foreground">Pending + Auto-Pilot Eligible</p>
//               </CardContent>
//             </Card>
//             <Card className="border-emerald-200 bg-emerald-50/30">
//               <CardContent className="p-4">
//                 <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-medium">Auto-Sent</span></div>
//                 <p className="text-2xl font-bold mt-1">{autoPilotStats.autoSent}</p>
//                 <p className="text-[11px] text-muted-foreground">Already sent via Auto-Pilot</p>
//               </CardContent>
//             </Card>
//             <Card className="border-amber-200 bg-amber-50/30">
//               <CardContent className="p-4">
//                 <div className="flex items-center gap-2 text-amber-700"><Clock className="h-4 w-4" /><span className="text-xs font-medium">Pending Review</span></div>
//                 <p className="text-2xl font-bold mt-1">{autoPilotStats.pendingReview}</p>
//                 <p className="text-[11px] text-muted-foreground">All drafts awaiting your action</p>
//               </CardContent>
//             </Card>
//             <Card>
//               <CardContent className="p-4">
//                 <div className="flex items-center gap-2 text-muted-foreground"><MessageCircleReply className="h-4 w-4" /><span className="text-xs font-medium">Total Drafts</span></div>
//                 <p className="text-2xl font-bold mt-1">{autoPilotStats.total}</p>
//                 <p className="text-[11px] text-muted-foreground">All reply drafts</p>
//               </CardContent>
//             </Card>
//           </div>

//           <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-white">
//             <CardHeader>
//               <CardTitle className="text-base flex items-center gap-2"><Bot className="h-4 w-4 text-violet-600" /> Auto-Pilot Reply Sender</CardTitle>
//               <CardDescription>
//                 Auto-send high-confidence positive replies. Drafts with category <code className="text-[10px] bg-muted px-1 py-0.5 rounded">hot_lead</code>, <code className="text-[10px] bg-muted px-1 py-0.5 rounded">warm_followup</code> with confidence ≥ 80% are eligible.
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex flex-wrap gap-2">
//                 <Button variant="outline" onClick={handlePreviewAutoPilot} disabled={apPreviewLoading}>
//                   {apPreviewLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
//                   Preview Auto-Pilot
//                 </Button>
//                 <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
//                   onClick={() => { if (autoPilotStats.eligible === 0) { toast.info('No eligible drafts to auto-send.'); return; } setApConfirmOpen(true); }}
//                   disabled={apSending}>
//                   {apSending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
//                   Run Auto-Pilot ({autoPilotStats.eligible} draft{autoPilotStats.eligible === 1 ? '' : 's'})
//                 </Button>
//               </div>
//               {apSending && (
//                 <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800 flex items-center gap-2">
//                   <Loader2 className="h-3.5 w-3.5 animate-spin" /> Auto-Pilot is sending eligible drafts via MailBridge…
//                 </div>
//               )}
//               <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
//                 <p className="font-medium flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> How Auto-Pilot works</p>
//                 <ol className="list-decimal list-inside space-y-0.5 ml-1">
//                   <li>Click <b>Preview Auto-Pilot</b> to dry-run — see exactly which drafts would be sent.</li>
//                   <li>Click <b>Run Auto-Pilot</b> to actually send. You'll get a confirmation dialog first.</li>
//                   <li>If MailBridge is connected, drafts are sent via your sending domain. Otherwise, drafts are marked <code className="text-[10px] bg-white px-1 py-0.5 rounded">auto_sent</code> but not delivered.</li>
//                   <li>Failed sends stay in <code className="text-[10px] bg-white px-1 py-0.5 rounded">pending</code> so you can retry manually.</li>
//                 </ol>
//               </div>
//             </CardContent>
//           </Card>
//         </TabsContent>

//         {/* ── Bounced Tab ───────────────────────────────────────────────── */}
//         <TabsContent value="bounced" className="mt-4">
//           {bouncedSequences.length === 0 ? (
//             <div className="flex flex-col items-center justify-center py-16 text-center">
//               <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-3" />
//               <p className="text-sm font-medium">No bounced emails</p>
//               <p className="text-xs text-muted-foreground mt-1">
//                 Bounces are detected automatically by the reply poller every 2 minutes.
//               </p>
//             </div>
//           ) : (
//             <div className="space-y-3">
//               <p className="text-xs text-muted-foreground">
//                 {bouncedSequences.length} bounced sequence{bouncedSequences.length === 1 ? '' : 's'} — these addresses could not be delivered to. Affected prospects are suppressed from future sends.
//               </p>
//               <div className="rounded-md border overflow-hidden">
//                 <table className="w-full text-xs">
//                   <thead className="bg-muted/40 border-b">
//                     <tr>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Subject</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Bounce Reason</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Bounced At</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {bouncedSequences.map((seq, idx) => (
//                       <tr key={seq.id} className={cn('border-b last:border-0', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
//                         <td className="p-3 font-medium">Touch {seq.touchNumber}</td>
//                         <td className="p-3">
//                           {seq.subjectLine
//                             ? <span className="truncate max-w-[200px] block">{seq.subjectLine}</span>
//                             : <span className="text-muted-foreground italic">—</span>}
//                         </td>
//                         <td className="p-3">
//                           <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] border border-red-200">
//                             {seq.bounceReason ?? 'unknown'}
//                           </span>
//                         </td>
//                         <td className="p-3 text-muted-foreground">
//                           {seq.bouncedAt ? new Date(seq.bouncedAt).toLocaleString() : '—'}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}
//         </TabsContent>

//         {/* ── Message Blocked Tab ────────────────────────────────────── */}
//         <TabsContent value="message-blocked" className="mt-4">
//           {messageBlockedSequences.length === 0 ? (
//             <div className="flex flex-col items-center justify-center py-16 text-center">
//               <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-3" />
//               <p className="text-sm font-medium">No blocked emails</p>
//               <p className="text-xs text-muted-foreground mt-1">
//                 Messages blocked by security gateways (e.g. Mimecast, Barracuda) will appear here.
//               </p>
//             </div>
//           ) : (
//             <div className="space-y-3">
//               <p className="text-xs text-muted-foreground">
//                 {messageBlockedSequences.length} blocked sequence{messageBlockedSequences.length === 1 ? '' : 's'} — these emails were rejected by the recipient&apos;s security gateway. Affected prospects are suppressed from future sends.
//               </p>
//               <div className="rounded-md border overflow-hidden">
//                 <table className="w-full text-xs">
//                   <thead className="bg-muted/40 border-b">
//                     <tr>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Subject</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Block Reason</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Blocked At</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {messageBlockedSequences.map((seq, idx) => (
//                       <tr key={seq.id} className={cn('border-b last:border-0', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
//                         <td className="p-3 font-medium">Touch {seq.touchNumber}</td>
//                         <td className="p-3">
//                           {seq.subjectLine
//                             ? <span className="truncate max-w-[200px] block">{seq.subjectLine}</span>
//                             : <span className="text-muted-foreground italic">—</span>}
//                         </td>
//                         <td className="p-3">
//                           <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] border border-orange-200">
//                             {seq.messageBlockedReason ?? 'unknown'}
//                           </span>
//                         </td>
//                         <td className="p-3 text-muted-foreground">
//                           {seq.messageBlockedAt ? new Date(seq.messageBlockedAt).toLocaleString() : '—'}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}
//         </TabsContent>

//         {/* ── Link Clicked Tab ─────────────────────────────────────────── */}
//         <TabsContent value="clicked" className="mt-4">
//           {uniqueClickGroups.length === 0 ? (
//             <div className="flex flex-col items-center justify-center py-16 text-center">
//               <MousePointerClick className="h-8 w-8 text-violet-400 mb-3" />
//               <p className="text-sm font-medium">No link clicks yet</p>
//               <p className="text-xs text-muted-foreground mt-1">
//                 Clicks on tracked links in your emails will show up here as they come in.
//               </p>
//             </div>
//           ) : (
//             <div className="space-y-3">
//               {/* Phase C: scanner toggle + count line */}
//               <div className="flex items-center justify-between">
//                 <p className="text-xs text-muted-foreground">
//                   {uniqueClickGroups.length} unique link{uniqueClickGroups.length === 1 ? '' : 's'} clicked — most recent first.
//                   {!showScanners && (
//                     <span className="ml-1 text-muted-foreground/70">(human clicks only)</span>
//                   )}
//                 </p>
//                 <button
//                   onClick={() => setShowScanners((v) => !v)}
//                   className={cn(
//                     'text-[11px] px-2 py-1 rounded border transition-colors',
//                     showScanners
//                       ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
//                       : 'bg-muted border-muted-foreground/20 text-muted-foreground hover:bg-muted/80'
//                   )}
//                 >
//                   {showScanners ? 'Hide scanners & bots' : 'Show suspected scanners & bots'}
//                 </button>
//               </div>

//               <div className="rounded-md border overflow-hidden">
//                 <table className="w-full text-xs">
//                   <thead className="bg-muted/40 border-b">
//                     <tr>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Prospect</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Email</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Last Clicked</th>
//                       <th className="text-left p-3 font-medium text-muted-foreground">Original Link</th>
//                       {SHOW_GA4_UI && (
//                         <th className="text-left p-3 font-medium text-muted-foreground">
//                           <span className="flex items-center gap-1">
//                             <Activity className="h-3 w-3" /> GA4 Engagement
//                           </span>
//                         </th>
//                       )}
//                       {showScanners && (
//                         <th className="text-left p-3 font-medium text-muted-foreground">Quality</th>
//                       )}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {uniqueClickGroups.map(({ data: c, count }, idx) => {
//                       const isScanner = c.clickQuality && c.clickQuality !== 'human';
//                       return (
//                         <tr
//                           key={`${c.touchNumber}|${c.prospectEmail}|${c.originalUrl}`}
//                           className={cn(
//                             'border-b last:border-0',
//                             isScanner ? 'opacity-50' : (idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')
//                           )}
//                         >
//                           <td className="p-3 font-medium">
//                             {c.touchNumber ? `Touch ${c.touchNumber}` : <span className="text-muted-foreground italic">—</span>}
//                           </td>
//                           <td className="p-3">
//                             <div className="flex items-center gap-1.5">
//                               {c.prospectName?.trim()
//                                 ? <span className="truncate max-w-[180px] block">{c.prospectName}</span>
//                                 : <span className="text-muted-foreground italic">—</span>}
//                               {count > 1 && (
//                                 <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold border border-violet-200 shrink-0">
//                                   {count}
//                                 </span>
//                               )}
//                             </div>
//                           </td>
//                           <td className="p-3">
//                             {c.prospectEmail
//                               ? <span className="truncate max-w-[220px] block">{c.prospectEmail}</span>
//                               : <span className="text-muted-foreground italic">—</span>}
//                           </td>
//                           <td className="p-3 text-muted-foreground">
//                             {c.clickedAt ? new Date(c.clickedAt).toLocaleString() : '—'}
//                           </td>
//                           <td className="p-3">
//                             {c.originalUrl ? (
//                               <a
//                                 href={c.originalUrl}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 hover:underline"
//                                 title={c.originalUrl}
//                               >
//                                 <span className="truncate max-w-[200px]">{c.originalUrl}</span>
//                                 <ExternalLink className="h-3 w-3 shrink-0" />
//                               </a>
//                             ) : (
//                               <span className="text-muted-foreground italic">—</span>
//                             )}
//                           </td>
//                           {SHOW_GA4_UI && <EngagementCell sequenceId={c.sequenceId} />}
//                           {showScanners && (
//                             <td className="p-3">
//                               {c.clickQuality === 'likely_scanner' && (
//                                 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
//                                   Suspected Scanner
//                                 </span>
//                               )}
//                               {c.clickQuality === 'bot' && (
//                                 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
//                                   Bot
//                                 </span>
//                               )}
//                               {(!c.clickQuality || c.clickQuality === 'human') && (
//                                 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
//                                   human
//                                 </span>
//                               )}
//                             </td>
//                           )}
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}
//         </TabsContent>
//       </Tabs>

//       {/* Log a Reply Dialog */}
//       <Dialog open={logOpen} onOpenChange={setLogOpen}>
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Log a Reply</DialogTitle>
//             <DialogDescription>Paste an external reply that wasn't captured by MailBridge.</DialogDescription>
//           </DialogHeader>
//           <div className="space-y-3">
//             <div className="space-y-1">
//               <Label>Prospect <span className="text-destructive">*</span></Label>
//               <Select value={logProspectId} onValueChange={(v) => { setLogProspectId(v); setLogSequenceId(''); }}>
//                 <SelectTrigger><SelectValue placeholder="Select prospect…" /></SelectTrigger>
//                 <SelectContent className="max-h-64">
//                   {prospects.map((p) => (
//                     <SelectItem key={p.id} value={p.id}>
//                       {p.firstName} {p.lastName}{p.company ? ` — ${p.company}` : ''}
//                     </SelectItem>
//                   ))}
//                 </SelectContent>
//               </Select>
//             </div>
//             <div className="space-y-1">
//               <Label>Sequence (Touch) <span className="text-destructive">*</span></Label>
//               <Select value={logSequenceId} onValueChange={setLogSequenceId} disabled={!logProspectId}>
//                 <SelectTrigger><SelectValue placeholder={logProspectId ? 'Select sequence…' : 'Select a prospect first'} /></SelectTrigger>
//                 <SelectContent className="max-h-64">
//                   {logSequences.map((s) => (
//                     <SelectItem key={s.id} value={s.id}>
//                       Touch {s.touchNumber} — {s.angle.replace(/_/g, ' ')}
//                     </SelectItem>
//                   ))}
//                 </SelectContent>
//               </Select>
//             </div>
//             <div className="space-y-1">
//               <Label>Reply Text <span className="text-destructive">*</span></Label>
//               <Textarea rows={5} value={logReplyText} onChange={(e) => setLogReplyText(e.target.value)} placeholder="Paste the prospect's reply here…" />
//             </div>
//           </div>
//           <DialogFooter>
//             <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
//             <Button disabled={!logReplyText.trim() || !logProspectId || !logSequenceId || logSubmitting} onClick={handleLogReply}>
//               {logSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Log Reply
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* Auto-Pilot Preview Dialog */}
//       <Dialog open={apPreviewOpen} onOpenChange={setApPreviewOpen}>
//         <DialogContent className="sm:max-w-2xl">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-violet-600" /> Auto-Pilot Preview (Dry-Run)</DialogTitle>
//             <DialogDescription>
//               {apPreviewLoading ? 'Loading…' : `${apPreview?.count ?? 0} draft${(apPreview?.count ?? 0) === 1 ? '' : 's'} would be sent.`}
//             </DialogDescription>
//           </DialogHeader>
//           {apPreviewLoading ? (
//             <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
//           ) : (
//             <div className="max-h-72 overflow-y-auto space-y-2">
//               {(apPreview?.eligible ?? []).length === 0
//                 ? <p className="text-sm text-muted-foreground text-center py-4">No eligible drafts.</p>
//                 : (apPreview?.eligible ?? []).map((d) => (
//                   <div key={d.id} className="p-3 rounded-lg border text-sm space-y-1">
//                     <div className="flex items-center justify-between">
//                       <div className="flex items-center gap-2">
//                         <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', CATEGORY_COLORS[getEffectiveCategory(d)] ?? 'bg-gray-100')}>
//                           {getCfg(getEffectiveCategory(d)).label}
//                         </span>
//                         <span className="text-xs font-medium">{d.prospectName ?? d.prospectEmail ?? 'Unknown'}</span>
//                       </div>
//                       <span className="text-xs text-muted-foreground">{d.confidence != null ? `${Math.round(d.confidence * 100)}% confidence` : ''}</span>
//                     </div>
//                     <p className="text-xs text-muted-foreground line-clamp-2">{d.draftBody ?? d.originalReply}</p>
//                   </div>
//                 ))
//               }
//             </div>
//           )}
//           <DialogFooter>
//             <Button variant="outline" onClick={() => setApPreviewOpen(false)}>Close</Button>
//             <Button onClick={() => { setApPreviewOpen(false); setApConfirmOpen(true); }} disabled={(apPreview?.count ?? 0) === 0}>
//               Proceed to Send All
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* Auto-Pilot Confirm Dialog */}
//       <Dialog open={apConfirmOpen} onOpenChange={setApConfirmOpen}>
//         <DialogContent className="sm:max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> Confirm Auto-Pilot Send</DialogTitle>
//             <DialogDescription>
//               This will send <strong>{autoPilotStats.eligible}</strong> eligible draft{autoPilotStats.eligible === 1 ? '' : 's'} via MailBridge. Cannot be undone.
//             </DialogDescription>
//           </DialogHeader>
//           <DialogFooter>
//             <Button variant="outline" onClick={() => setApConfirmOpen(false)}>Cancel</Button>
//             <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleRunAutoPilot} disabled={apSending}>
//               {apSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Run Auto-Pilot
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* Auto-Pilot Result Dialog */}
//       <Dialog open={apResultOpen} onOpenChange={setApResultOpen}>
//         <DialogContent className="sm:max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2">
//               {(apResult?.failed ?? 0) === 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
//               Auto-Pilot Results
//             </DialogTitle>
//             <DialogDescription>Send run complete.</DialogDescription>
//           </DialogHeader>
//           {apResult && (
//             <div className="grid grid-cols-3 gap-4 py-2">
//               {[
//                 { label: 'Sent', value: apResult.sent, color: 'text-emerald-600' },
//                 { label: 'Failed', value: apResult.failed, color: 'text-red-600' },
//                 { label: 'Total', value: apResult.total, color: 'text-foreground' },
//               ].map((s) => (
//                 <div key={s.label} className="text-center p-3 rounded-lg border">
//                   <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
//                   <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
//                 </div>
//               ))}
//             </div>
//           )}
//           <DialogFooter><Button onClick={() => setApResultOpen(false)}>Done</Button></DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* v4: Reclassify Modal */}
//       <ReclassifyModal
//         open={reclassifyOpen}
//         draft={selectedDraft}
//         onClose={() => setReclassifyOpen(false)}
//         onConfirm={handleReclassifyConfirm}
//         submitting={reclassifySubmitting}
//       />

//       {/* v4: SAV Alerts Modal */}
//       <SAVAlertsModal
//         open={savAlertsOpen}
//         onClose={() => setSavAlertsOpen(false)}
//         alerts={savAlertsList}
//         onAcknowledge={handleAcknowledgeAlert}
//         acknowledging={acknowledgingAlert}
//       />
//     </div>
//   );
// }

import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot, CheckCircle2, Send, Sparkles, Eye, Info,
  Loader2, MessageCircleReply, Zap, Ban, DollarSign, Clock,
  ShieldAlert, XCircle, Mail, User, ArrowDown, AlertTriangle,
  Building2, RefreshCw, Search, Flame, TreePine, HelpCircle,
  Coffee, Edit2, TriangleAlert, MousePointerClick, ExternalLink,
  ShieldX, Activity, Wifi, WifiOff,
} from 'lucide-react';
import {
  useSequenceEngagement,
  isSequenceEngaged,
  totalVisibleSeconds,
  formatDwellTime,
} from '@/features/engagement/useEngagement';
import { toast } from 'sonner';

import { http } from '@/services/apiClient';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { SendingDomainNotice } from '@/components/sending/SendingDomainNotice';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReplyDraft {
  id: string;
  sequenceId: string;
  prospectId: string;
  originalReply: string;
  category: string;
  summary: string | null;
  suggestedAction: string | null;
  draftBody: string | null;
  status: string;
  sentAt: string | null;
  autoPilotEligible: boolean;
  confidence: number | null;
  autoSentAt: string | null;
  snoozedUntil: string | null;
  wrongCompanyHandledAt: string | null;
  extraData: string | null;
  // v4 reclassification fields
  manualCategory: string | null;
  manualCategoryBy: string | null;
  manualCategoryAt: string | null;
  manualCategoryNote: string | null;
  isHumanReviewed: boolean;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from backend join
  prospectName: string | null;
  prospectEmail: string | null;
  sentEmailSubject: string | null;
  sentEmailBody: string | null;
  // Association — which campaign / touch / rep / mailbox / domain the reply belongs to
  campaignId?: string | null;
  campaignName?: string | null;
  touchNumber?: number | null;
  repUserId?: string | null;
  /** Mailbox the touch was sent from = the inbox the reply arrived in. */
  sentFromEmail?: string | null;
  sendingDomain?: string | null;
  sendingDomainRegistered?: boolean | null;
}

const ALL_FILTER = '__all__';

/** "Q4 Outreach · Touch 2 · user1@newco.com" — the reply's association line. */
function associationLine(d: ReplyDraft): string {
  return [
    d.campaignName ?? null,
    d.touchNumber ? `Touch ${d.touchNumber}` : null,
    d.sentFromEmail ?? null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface AutoPilotPreview {
  eligible: ReplyDraft[];
  count: number;
}

interface AutoPilotSendResult {
  sent: number;
  failed: number;
  markedOnly: number;
  total: number;
}

interface Prospect {
  id: string;
  firstName: string;
  lastName: string;
  company: string | null;
}

interface TrackingSummary {
  period_days: number;
  total_sent: number;
  total_replied: number;
  total_bounced: number;
  reply_rate: number;
  bounce_rate: number;
  top_bounce_reasons: Array<{ reason: string; count: number }>;
  by_status: Record<string, number>;
}

interface BouncedSequence {
  id: string;
  status: string;
  bouncedAt: string | null;
  bounceReason: string | null;
  prospectId: string;
  subjectLine: string | null;
  touchNumber: number;
}

interface MessageBlockedSequence {
  id: string;
  status: string;
  messageBlockedAt: string | null;
  messageBlockedReason: string | null;
  prospectId: string;
  subjectLine: string | null;
  touchNumber: number;
}

interface ClickedSequence {
  id: string;
  sequenceId: string;
  touchNumber: number;
  prospectId: string;
  prospectName: string;
  prospectEmail: string | null;
  originalUrl: string;
  clickedAt: string;
  /** Phase C: 'human' | 'likely_scanner' | 'bot' */
  clickQuality?: string;
}

interface InboxStats {
  total: number;
  hotLeads: number;
  warmFollowups: number;
  ooo: number;
  negative: number;
  wrongCompany: number;
  bounce: number;
  unsubscribe: number;
  curious: number;
  other: number;
  actionRequired: number;
  suppressed: number;
  positiveReplyRate: number;
  autoSent: number;
  unacknowledgedSavAlerts: number;
}

interface SAVAlert {
  id: string;
  replyDraftId: string;
  detectedAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  notes: string | null;
  severity: string;
}

interface SAVAlertsListResponse {
  alerts: SAVAlert[];
  unacknowledgedCount: number;
}

interface CategorizeResult {
  category: string;
  confidence: number;
  summary: string;
  suggestedAction: string;
  promptVersion: string;
  returnDate: string | null;
  newCompany: string | null;
  referralName: string | null;
  bounceType: string | null;
}

interface ActionResult {
  ok: boolean;
  message: string;
  actionTaken: string;
  draftId: string | null;
}

interface ReclassifyResult {
  ok: boolean;
  message: string;
  draftId: string;
  effectiveCategory: string;
  savAlertId: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, {
  label: string;
  icon: React.ReactNode;
  bgColor: string;
  borderColor: string;
  textColor: string;
  actionLabel: string;
  actionVariant: 'default' | 'destructive' | 'outline' | 'secondary';
  playbook: string[];
}> = {
  hot_lead: {
    label: 'Hot Lead',
    icon: <Flame className="h-3 w-3" />,
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700',
    actionLabel: 'Create Deal & Approve',
    actionVariant: 'default',
    playbook: ['Reply within 2 hours', 'Send calendar link immediately', 'Enter into CRM', 'Remove from sequence'],
  },
  warm_followup: {
    label: 'Warm Follow-Up',
    icon: <TreePine className="h-3 w-3" />,
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    textColor: 'text-teal-700',
    actionLabel: 'Approve & Continue Sequence',
    actionVariant: 'default',
    playbook: ['Answer any questions asked', 'Ask one qualifying question back', 'Continue the sequence'],
  },
  curious: {
    label: 'Curious',
    icon: <HelpCircle className="h-3 w-3" />,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    actionLabel: 'Add Value & Continue',
    actionVariant: 'default',
    playbook: ['Acknowledge briefly (1-2 sentences)', 'Add value with one specific detail', 'Continue in sequence'],
  },
  ooo: {
    label: 'Out of Office',
    icon: <Coffee className="h-3 w-3" />,
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    textColor: 'text-sky-700',
    actionLabel: 'Snooze to Return Date',
    actionVariant: 'secondary',
    playbook: ['Pause all sequences for this prospect', 'Resume 2-3 days after return date'],
  },
  negative: {
    label: 'Not Interested',
    icon: <XCircle className="h-3 w-3" />,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    actionLabel: 'Suppress Prospect',
    actionVariant: 'destructive',
    playbook: ['Reply briefly if soft no', 'Suppress from this and all future campaigns', 'Never argue or pressure'],
  },
  bounce: {
    label: 'Bounce',
    icon: <Zap className="h-3 w-3" />,
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    actionLabel: 'Remove from List',
    actionVariant: 'destructive',
    playbook: ['Hard bounce: remove permanently', 'Soft bounce: pause 7 days, retry once'],
  },
  wrong_company: {
    label: 'Wrong Company',
    icon: <Building2 className="h-3 w-3" />,
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-700',
    actionLabel: 'Mark & Suppress',
    actionVariant: 'secondary',
    playbook: ['Suppress this prospect', 'Find their replacement', 'Add referral as new prospect if named'],
  },
  unsubscribe: {
    label: 'Unsubscribe',
    icon: <Ban className="h-3 w-3" />,
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    textColor: 'text-rose-700',
    actionLabel: 'Withdraw Consent',
    actionVariant: 'destructive',
    playbook: ['Do not reply', 'Suppress immediately from all campaigns', 'This action is permanent (GDPR)'],
  },
  action_required: {
    label: 'Action Required',
    icon: <TriangleAlert className="h-3 w-3" />,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    textColor: 'text-amber-800',
    actionLabel: 'Acknowledge & Act',
    actionVariant: 'secondary',
    playbook: [
      'This is a Sender Address Verification (SAV) challenge — not a prospect reply',
      'Check your SPF/DKIM/DMARC records',
      'Contact your domain or mail provider if deliverability is blocked',
      'Acknowledge the alert once you have taken action',
    ],
  },
  suppressed: {
    label: 'Suppressed',
    icon: <Ban className="h-3 w-3" />,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    textColor: 'text-red-800',
    actionLabel: 'Already Suppressed',
    actionVariant: 'destructive',
    playbook: ['Prospect has been manually suppressed', 'No further outreach will be sent', 'Contact is excluded from all future campaigns'],
  },
  other: {
    label: 'Uncategorized',
    icon: <MessageCircleReply className="h-3 w-3" />,
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-600',
    actionLabel: 'Approve for Manual Review',
    actionVariant: 'outline',
    playbook: ['Review manually', 'Re-classify if needed'],
  },
};

const LEGACY_CATEGORY_DISPLAY: Record<string, string> = {
  interested: 'hot_lead',
  meeting_request: 'hot_lead',
  demo_request: 'hot_lead',
  positive_reply: 'warm_followup',
  positive_signal: 'warm_followup',
  positive: 'warm_followup',
  negative_reply: 'negative',
  not_interested: 'negative',
  out_of_office: 'ooo',
  oof: 'ooo',
  needs_info: 'curious',
  counter_proposal: 'curious',
  neutral: 'curious',
};

const CATEGORY_COLORS: Record<string, string> = {
  // v2 names
  hot_lead:      'bg-emerald-100 text-emerald-700',
  warm_followup: 'bg-teal-100 text-teal-700',
  curious:       'bg-blue-100 text-blue-700',
  ooo:           'bg-sky-100 text-sky-700',
  negative:      'bg-red-100 text-red-700',
  bounce:        'bg-orange-100 text-orange-700',
  wrong_company: 'bg-purple-100 text-purple-700',
  unsubscribe:   'bg-rose-100 text-rose-700',
  other:         'bg-gray-100 text-gray-700',
  // v4 human-only categories
  action_required: 'bg-amber-100 text-amber-800',
  suppressed:      'bg-red-100 text-red-800',
  // legacy names
  interested:      'bg-emerald-100 text-emerald-700',
  positive_signal: 'bg-green-100 text-green-700',
  meeting_request: 'bg-blue-100 text-blue-700',
  needs_info:      'bg-amber-100 text-amber-700',
  counter_proposal:'bg-purple-100 text-purple-700',
  neutral:         'bg-gray-100 text-gray-700',
  out_of_office:   'bg-sky-100 text-sky-700',
  not_interested:  'bg-red-100 text-red-700',
  positive:        'bg-emerald-100 text-emerald-700',
  objection:       'bg-amber-100 text-amber-700',
  oof:             'bg-sky-100 text-sky-700',
};

const STATUS_COLORS: Record<string, string> = {
  pending:        'bg-amber-100 text-amber-700',
  pending_review: 'bg-orange-100 text-orange-700',
  approved:       'bg-blue-100 text-blue-700',
  sent:           'bg-emerald-100 text-emerald-700',
  dismissed:      'bg-gray-100 text-gray-600',
  auto_sent:      'bg-violet-100 text-violet-700',
  handled:        'bg-emerald-100 text-emerald-700',
};

const DRAFT_STATUS_LABEL: Record<string, string> = {
  pending:        'Pending',
  pending_review: 'Pending Review',
  approved:       'Approved',
  sent:           'Sent',
  dismissed:      'Dismissed',
  auto_sent:      'Auto Sent',
  handled:        'Handled',
};

const POSITIVE_CATEGORIES = [
  'interested', 'meeting_request', 'positive_signal', 'positive',
  'hot_lead', 'warm_followup',
];

const FILTER_PILLS: Array<{ key: string; label: string }> = [
  { key: 'all',            label: 'All' },
  { key: 'hot_lead',       label: '🔥 Hot Lead' },
  { key: 'warm_followup',  label: '🌱 Warm' },
  { key: 'curious',        label: '🤔 Curious' },
  { key: 'ooo',            label: '☕ OOO' },
  { key: 'negative',       label: '❌ Negative' },
  { key: 'bounce',         label: '⚡ Bounce' },
  { key: 'wrong_company',  label: '🏢 Wrong Co.' },
  { key: 'unsubscribe',    label: '🚫 Unsub' },
  { key: 'action_required',label: '⚠️ Action Req.' },
  { key: 'suppressed',     label: '🔇 Suppressed' },
  { key: 'other',          label: 'Other' },
];

// Categories selectable in the override dropdown
const RECLASSIFY_OPTIONS: Array<{ value: string; label: string; group: 'human' | 'ai' }> = [
  // Human-only (highlighted)
  { value: 'action_required', label: '⚠️ Action Required (SAV / Infrastructure)', group: 'human' },
  { value: 'suppressed',      label: '🔇 Mark as Suppressed',                      group: 'human' },
  // AI corrections
  { value: 'hot_lead',        label: '🔥 Hot Lead',       group: 'ai' },
  { value: 'warm_followup',   label: '🌱 Warm Follow-Up', group: 'ai' },
  { value: 'curious',         label: '🤔 Curious',        group: 'ai' },
  { value: 'ooo',             label: '☕ Out of Office',   group: 'ai' },
  { value: 'negative',        label: '❌ Not Interested',  group: 'ai' },
  { value: 'bounce',          label: '⚡ Bounce',          group: 'ai' },
  { value: 'wrong_company',   label: '🏢 Wrong Company',  group: 'ai' },
  { value: 'unsubscribe',     label: '🚫 Unsubscribe',    group: 'ai' },
  { value: 'other',           label: 'Uncategorized',      group: 'ai' },
];

function normalise<T>(data: unknown): T[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as T[];
  return ((data as { items?: T[] }).items) ?? [];
}

function getEffectiveCategory(draft: ReplyDraft): string {
  return draft.manualCategory || draft.category;
}

function getCfg(category: string) {
  const normalised = LEGACY_CATEGORY_DISPLAY[category] ?? category;
  return CATEGORY_CONFIG[normalised] ?? CATEGORY_CONFIG['other'];
}

function parseExtra(extraData: string | null): Record<string, string> {
  if (!extraData) return {};
  try { return JSON.parse(extraData); } catch { return {}; }
}

// ─── Conversation Thread Component ────────────────────────────────────────────

function ConversationThread({ draft }: { draft: ReplyDraft }) {
  const prospectLabel = draft.prospectName ?? draft.prospectEmail ?? 'Prospect';

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          <Mail className="h-3 w-3" /> You sent
        </div>
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          {draft.sentEmailSubject && (
            <p className="font-medium text-xs text-muted-foreground mb-1.5">
              Subject: {draft.sentEmailSubject}
            </p>
          )}
          {draft.sentEmailBody ? (
            (() => {
              const isHtml = /<[a-z][\s\S]*>/i.test(draft.sentEmailBody!);
              return isHtml ? (
                <div
                  className="text-sm leading-relaxed prose prose-sm max-w-none [&_a]:text-blue-600 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_strong]:font-semibold [&_p]:mb-2 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: draft.sentEmailBody! }}
                />
              ) : (
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.sentEmailBody}</p>
              );
            })()
          ) : (
            <span className="italic text-muted-foreground text-sm">
              Email body not captured — check Sequence details.
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center text-muted-foreground/40">
        <ArrowDown className="h-4 w-4" />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          <User className="h-3 w-3" />
          <span>{prospectLabel} replied</span>
          {draft.prospectEmail && (
            <span className="normal-case font-normal text-muted-foreground/60">
              &lt;{draft.prospectEmail}&gt;
            </span>
          )}
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.originalReply}</p>
        </div>
      </div>

      {draft.draftBody && (
        <div className="flex items-center justify-center text-muted-foreground/40">
          <ArrowDown className="h-4 w-4" />
        </div>
      )}

      {draft.draftBody && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            <Sparkles className="h-3 w-3 text-emerald-600" />
            <span className="text-emerald-700">AI-Generated Draft</span>
            {draft.suggestedAction && (
              <span className="normal-case font-normal text-muted-foreground/60 ml-1">
                · {draft.suggestedAction}
              </span>
            )}
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft.draftBody}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Category Badge with Audit Tooltip ───────────────────────────────────────

function CategoryBadge({ draft, size = 'sm' }: { draft: ReplyDraft; size?: 'sm' | 'xs' }) {
  const effectiveCat = getEffectiveCategory(draft);
  const cfg = getCfg(effectiveCat);
  const isHumanSet = !!draft.manualCategory;
  const px = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';

  const badge = (
    <span className={cn(px, 'rounded font-medium flex items-center gap-1', CATEGORY_COLORS[effectiveCat] ?? 'bg-gray-100')}>
      {isHumanSet && <Edit2 className="h-2.5 w-2.5 opacity-60" />}
      {cfg.label}
    </span>
  );

  if (!isHumanSet) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs space-y-1">
          <p className="font-semibold">Manually reviewed</p>
          {draft.manualCategoryAt && (
            <p className="text-muted-foreground">
              Set on {new Date(draft.manualCategoryAt).toLocaleString()}
            </p>
          )}
          {draft.manualCategoryNote && (
            <p className="italic">{draft.manualCategoryNote}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── SAV Alert Banner ─────────────────────────────────────────────────────────

function SAVAlertBanner({
  count,
  onViewAlerts,
}: {
  count: number;
  onViewAlerts: () => void;
}) {
  if (count === 0) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-center gap-3">
      <TriangleAlert className="h-4 w-4 text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">
          Action Required: {count} sender verification challenge{count === 1 ? '' : 's'} detected
        </p>
        <p className="text-xs text-amber-700 mt-0.5">
          These are infrastructure emails — not prospect replies. Review and acknowledge each one.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
        onClick={onViewAlerts}
      >
        View Alerts →
      </Button>
    </div>
  );
}

// ─── SAV Alerts Modal ─────────────────────────────────────────────────────────

function SAVAlertsModal({
  open,
  onClose,
  alerts,
  onAcknowledge,
  acknowledging,
}: {
  open: boolean;
  onClose: () => void;
  alerts: SAVAlert[];
  onAcknowledge: (alertId: string, notes: string) => void;
  acknowledging: string | null;
}) {
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 text-amber-600" />
            Sender Verification Alerts
          </DialogTitle>
          <DialogDescription>
            These emails are Sender Address Verification (SAV) challenges from mail servers — not replies from prospects.
            Review each one and acknowledge once you have taken the required action.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-2" />
              <p className="text-sm font-medium">All alerts acknowledged</p>
            </div>
          ) : alerts.map((alert) => (
            <div
              key={alert.id}
              className={cn(
                'rounded-lg border p-4 space-y-3',
                alert.severity === 'critical'
                  ? 'border-red-300 bg-red-50'
                  : 'border-amber-200 bg-amber-50/50',
                alert.acknowledgedAt && 'opacity-60'
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge
                    className={cn(
                      'text-[10px]',
                      alert.severity === 'critical'
                        ? 'bg-red-100 text-red-800 border-red-300'
                        : 'bg-amber-100 text-amber-800 border-amber-300'
                    )}
                  >
                    {alert.severity === 'critical' ? '🔴 Critical' : '🟡 Warning'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Detected {new Date(alert.detectedAt).toLocaleString()}
                  </span>
                </div>
                {alert.acknowledgedAt && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                    <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Acknowledged
                  </Badge>
                )}
              </div>
              {alert.notes && (
                <p className="text-xs text-muted-foreground italic">{alert.notes}</p>
              )}
              {!alert.acknowledgedAt && (
                <div className="space-y-2">
                  <Textarea
                    rows={2}
                    className="text-xs"
                    placeholder="Notes on action taken (e.g. updated SPF record, whitelisted IP)…"
                    value={notesMap[alert.id] ?? ''}
                    onChange={(e) => setNotesMap((m) => ({ ...m, [alert.id]: e.target.value }))}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => onAcknowledge(alert.id, notesMap[alert.id] ?? '')}
                    disabled={acknowledging === alert.id}
                  >
                    {acknowledging === alert.id
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    }
                    Acknowledge
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reclassify Modal ─────────────────────────────────────────────────────────

function ReclassifyModal({
  open,
  draft,
  onClose,
  onConfirm,
  submitting,
}: {
  open: boolean;
  draft: ReplyDraft | null;
  onClose: () => void;
  onConfirm: (category: string, note: string) => void;
  submitting: boolean;
}) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) { setSelectedCategory(''); setNote(''); }
  }, [open]);

  if (!draft) return null;

  const isDestructive = ['suppressed', 'action_required'].includes(selectedCategory);
  const noteRequired = selectedCategory === 'action_required';
  const canConfirm = !!selectedCategory && (!noteRequired || note.trim().length > 0) && !submitting;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="h-4 w-4" /> Override Category
          </DialogTitle>
          <DialogDescription>
            Override the AI-assigned category for this reply.
            The original AI classification is preserved for audit purposes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>New Category</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category…" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Human Override
                </div>
                {RECLASSIFY_OPTIONS.filter((o) => o.group === 'human').map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
                <div className="px-2 py-1 mt-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Correct AI Category
                </div>
                {RECLASSIFY_OPTIONS.filter((o) => o.group === 'ai').map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCategory === 'action_required' && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <TriangleAlert className="h-3 w-3" /> Action Required selected
              </p>
              <p>This marks the reply as a Sender Address Verification challenge. No prospect action will be taken. A note describing the required action is mandatory.</p>
            </div>
          )}

          {selectedCategory === 'suppressed' && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <ShieldAlert className="h-3 w-3" /> Suppression selected
              </p>
              <p>This will immediately suppress the prospect from all future outreach. This action cannot be undone through the UI.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>
              Note{noteRequired && <span className="text-destructive ml-1">*</span>}
              {!noteRequired && <span className="text-muted-foreground text-xs ml-1">(optional)</span>}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                selectedCategory === 'action_required'
                  ? 'Describe the action to take (e.g. "SAV challenge from Microsoft EOP — whitelist our IP")…'
                  : 'Reason for override (e.g. "AI misread sarcasm", "Reply in French")…'
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant={isDestructive ? 'destructive' : 'default'}
            onClick={() => onConfirm(selectedCategory, note)}
            disabled={!canConfirm}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isDestructive ? 'Confirm Override' : 'Save Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── GA4 UI visibility switch ─────────────────────────────────────────────────
// Hidden until client GA4 access is confirmed. All GA4 code below stays in
// place — set this to true to show the sync strip and the GA4 Engagement
// column again. While false, no GA4 network calls are made from this page.
const SHOW_GA4_UI = true;

// ─── GA4 Engagement Cell ──────────────────────────────────────────────────────

function EngagementCell({ sequenceId }: { sequenceId: string | null | undefined }) {
  const { data: summaries, isLoading } = useSequenceEngagement(sequenceId);

  if (isLoading) {
    return <td className="p-3"><span className="inline-block w-16 h-4 rounded bg-muted/40 animate-pulse" /></td>;
  }
  if (!summaries || summaries.length === 0) {
    return <td className="p-3 text-muted-foreground text-[11px]">No data</td>;
  }

  const engaged = isSequenceEngaged(summaries);
  const seconds  = totalVisibleSeconds(summaries);

  if (!engaged && seconds === 0) {
    return <td className="p-3 text-muted-foreground text-[11px]">No data</td>;
  }

  return (
    <td className="p-3">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap',
          engaged
            ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400'
            : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
        )}
        title={`${summaries.reduce((a, s) => a + s.totalSessions, 0)} session(s) recorded`}
      >
        {engaged ? (
          <CheckCircle2 className="h-3 w-3" />
        ) : (
          <Activity className="h-3 w-3" />
        )}
        {engaged ? `Read · ${formatDwellTime(seconds)}` : `Opened · ${formatDwellTime(seconds)}`}
      </span>
    </td>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ReplyInboxPage() {
  const qc = useQueryClient();
  const { hasRole } = useAuth();
  const isManager = hasRole('MANAGER');
  // Reps always see only replies to their own emails (enforced server-side);
  // managers/admins can switch between everyone's and their own.
  const [replyScope, setReplyScope] = useState<'all' | 'mine'>('all');
  const [campaignFilter, setCampaignFilter] = useState<string>(ALL_FILTER);
  const [mailboxFilter, setMailboxFilter] = useState<string>(ALL_FILTER);

  const { data: rawReplies, isLoading } = useQuery({
    queryKey: ['reply-drafts', replyScope],
    queryFn: () => http.get<unknown>('/api/v1/reply-drafts', { scope: replyScope }),
    refetchInterval: 30_000,
    retry: false,
  });
  const replies = normalise<ReplyDraft>(rawReplies);

  const { data: autoPilotData } = useQuery({
    queryKey: ['reply-drafts-autopilot'],
    queryFn: () => http.get<AutoPilotPreview>('/api/v1/reply-drafts/auto-pilot'),
    retry: false,
  });

  const { data: inboxStats, refetch: refetchStats } = useQuery<InboxStats>({
    queryKey: ['reply-drafts-stats', replyScope],
    queryFn: () => http.get<InboxStats>('/api/v1/reply-drafts/stats', { scope: replyScope }),
    refetchInterval: 30_000,
    retry: false,
  });

  const { data: savAlertsData, refetch: refetchSavAlerts } = useQuery<SAVAlertsListResponse>({
    queryKey: ['sav-alerts'],
    queryFn: () => http.get<SAVAlertsListResponse>('/api/v1/reply-drafts/sav-alerts'),
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: rawProspects } = useQuery({
    queryKey: ['prospects-mini'],
    queryFn: () => http.get<unknown>('/api/v1/prospects', { page: 1, page_size: 200 }),
    staleTime: 60_000,
  });
  const prospects = normalise<Prospect>(rawProspects);

  const [trackingDays, setTrackingDays] = useState(30);
  const [showScanners, setShowScanners] = useState(false);

  const { data: trackingSummary, isLoading: trackingLoading } = useQuery<TrackingSummary>({
    queryKey: ['tracking-summary', trackingDays],
    queryFn: () => http.get<TrackingSummary>('/api/v1/analytics/tracking-summary', { days: trackingDays }),
    staleTime: 60_000,
    retry: false,
  });

  const { data: rawBounced } = useQuery({
    queryKey: ['sequences-bounced'],
    queryFn: () => http.get<unknown>('/api/v1/sequences', { status: 'Bounced', limit: 200 }),
    staleTime: 60_000,
    retry: false,
  });
  const bouncedSequences = normalise<BouncedSequence>(rawBounced)
    .sort((a, b) => {
      const da = a.bouncedAt ? new Date(a.bouncedAt).getTime() : 0;
      const db_ = b.bouncedAt ? new Date(b.bouncedAt).getTime() : 0;
      return db_ - da; // descending — latest first
    });

  const { data: rawMessageBlocked } = useQuery({
    queryKey: ['sequences-message-blocked'],
    queryFn: () => http.get<unknown>('/api/v1/sequences', { status: 'MessageBlocked', limit: 200 }),
    staleTime: 60_000,
    retry: false,
  });
  const messageBlockedSequences = normalise<MessageBlockedSequence>(rawMessageBlocked)
    .sort((a, b) => {
      const da = a.messageBlockedAt ? new Date(a.messageBlockedAt).getTime() : 0;
      const db_ = b.messageBlockedAt ? new Date(b.messageBlockedAt).getTime() : 0;
      return db_ - da; // descending — latest first
    });

  const { data: rawClicked } = useQuery({
    queryKey: ['click-tracking-clicked-sequences', showScanners],
    queryFn: () => http.get<unknown>('/api/v1/click-tracking/clicked-sequences', {
      limit: 200,
      quality: showScanners ? 'all' : 'human',
    }),
    staleTime: 60_000,
    retry: false,
  });
  const clickedSequences = normalise<ClickedSequence>(rawClicked);

  // Deduplicate by (touchNumber, prospectEmail, originalUrl) — one row per unique combo, count = how many times clicked
  const uniqueClickGroups = useMemo(() => {
    const map = new Map<string, { data: ClickedSequence; count: number }>();
    for (const c of clickedSequences) {
      const key = `${c.touchNumber}|${c.prospectEmail ?? ''}|${c.originalUrl}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        // Keep the most recent clickedAt
        if (new Date(c.clickedAt) > new Date(existing.data.clickedAt)) {
          existing.data = { ...existing.data, clickedAt: c.clickedAt };
        }
      } else {
        map.set(key, { data: c, count: 1 });
      }
    }
    return Array.from(map.values());
  }, [clickedSequences]);

  const [selectedDraft, setSelectedDraft]         = useState<ReplyDraft | null>(null);
  const [showAutoPilotOnly, setShowAutoPilotOnly] = useState(false);
  const [categoryFilter, setCategoryFilter]       = useState<string>('all');
  const [createDealOpen, setCreateDealOpen]       = useState(false);
  const [dealTitle, setDealTitle]                 = useState('');
  const [dealValue, setDealValue]                 = useState('');
  const [dealNotes, setDealNotes]                 = useState('');
  const [dealNextAction, setDealNextAction]       = useState('');
  const [dealSuggesting, setDealSuggesting]       = useState(false);

  const [logOpen, setLogOpen]             = useState(false);
  const [logProspectId, setLogProspectId] = useState('');
  const [logSequenceId, setLogSequenceId] = useState('');
  const [logReplyText, setLogReplyText]   = useState('');
  const [logSubmitting, setLogSubmitting] = useState(false);

  // v4 reclassify state
  const [reclassifyOpen, setReclassifyOpen]     = useState(false);
  const [reclassifySubmitting, setReclassifySubmitting] = useState(false);

  // v4 SAV alerts modal state
  const [savAlertsOpen, setSavAlertsOpen]       = useState(false);
  const [acknowledgingAlert, setAcknowledgingAlert] = useState<string | null>(null);

  const { data: logSequencesRaw } = useQuery({
    queryKey: ['sequences-for-log', logProspectId],
    queryFn: () => http.get<unknown>('/api/v1/sequences', { prospect_id: logProspectId, limit: 50 }),
    enabled: !!logProspectId,
    staleTime: 30_000,
  });
  const logSequences = normalise<{ id: string; touchNumber: number; angle: string }>(logSequencesRaw);

  const [apPreviewOpen, setApPreviewOpen]       = useState(false);
  const [apPreviewLoading, setApPreviewLoading] = useState(false);
  const [apPreview, setApPreview]               = useState<AutoPilotPreview | null>(null);
  const [apConfirmOpen, setApConfirmOpen]       = useState(false);
  const [apSending, setApSending]               = useState(false);
  const [apResult, setApResult]                 = useState<AutoPilotSendResult | null>(null);
  const [apResultOpen, setApResultOpen]         = useState(false);

  // Apply the association filters first so category-pill counts and the
  // visible reply list are calculated from the same set of replies.
  const associationFilteredReplies = useMemo(() => {
    let list = replies;
    if (campaignFilter !== ALL_FILTER) {
      list = list.filter((r) => r.campaignId === campaignFilter);
    }
    if (mailboxFilter !== ALL_FILTER) {
      list = list.filter((r) => (r.sentFromEmail ?? '').trim().toLowerCase() === mailboxFilter.trim().toLowerCase());
    }
    return list;
  }, [replies, campaignFilter, mailboxFilter]);

  const filteredDrafts = useMemo(() => {
    let list = showAutoPilotOnly
      ? associationFilteredReplies.filter((r) => r.autoPilotEligible)
      : associationFilteredReplies;
    if (categoryFilter !== 'all') {
      list = list.filter((r) => {
        const effectiveCat = getEffectiveCategory(r);
        const normalised = LEGACY_CATEGORY_DISPLAY[effectiveCat] ?? effectiveCat;
        // Use effectiveCat (respects manualCategory override) — do NOT fall back to
        // r.category, which would re-count replies that have been manually re-classified.
        return normalised === categoryFilter || effectiveCat === categoryFilter;
      });
    }
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [associationFilteredReplies, showAutoPilotOnly, categoryFilter]);

  // Options for the association filters, built from the loaded replies.
  const campaignOptions = useMemo(() => {
    const m = new Map<string, string>();
    replies.forEach((r) => { if (r.campaignId) m.set(r.campaignId, r.campaignName ?? r.campaignId); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [replies]);
  const mailboxOptions = useMemo(() => {
    const m = new Map<string, { email: string; domain: string | null; registered: boolean | null }>();
    replies.forEach((r) => {
      if (r.sentFromEmail) {
        m.set(r.sentFromEmail.toLowerCase(), {
          email: r.sentFromEmail,
          domain: r.sendingDomain ?? null,
          registered: r.sendingDomainRegistered ?? null,
        });
      }
    });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [replies]);

  const autoPilotStats = useMemo(() => ({
    eligible:      replies.filter((r) => r.autoPilotEligible && r.status === 'pending').length,
    autoSent:      replies.filter((r) => r.status === 'auto_sent').length,
    pendingReview: replies.filter((r) => r.status === 'pending').length,
    total:         replies.length,
  }), [replies]);

  const pendingCount = replies.filter((r) => r.status === 'pending').length;

  // ── GA4 Sync (inline) ────────────────────────────────────────────────────────
  const [ga4SyncResult, setGa4SyncResult] = useState<{
    rows_upserted: number; rows_skipped: number; errors: number; message: string;
  } | null>(null);
  const [ga4LastSync, setGa4LastSync]  = useState<Date | null>(null);

  const { data: ga4Params } = useQuery({
    queryKey: ['sys-params-ga4'],
    queryFn: () => http.get<Array<{ key: string; value: string }>>('/api/v1/system-params'),
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    enabled: SHOW_GA4_UI,
  });
  const ga4Enabled = useMemo(() => {
    const items = ga4Params ?? [];
    return items.find((p) => p.key === 'ga4.enabled')?.value === 'true';
  }, [ga4Params]);
  const ga4HasPropertyId = useMemo(() => {
    const items = ga4Params ?? [];
    return (items.find((p) => p.key === 'ga4.property_id')?.value ?? '').trim().length > 0;
  }, [ga4Params]);

  const ga4SyncMut = useMutation({
    mutationFn: () => http.post<{ rows_upserted: number; rows_skipped: number; errors: number; message: string }>(
      '/api/v1/admin/ga4/sync', {}
    ),
    onSuccess: (result) => {
      setGa4SyncResult(result);
      setGa4LastSync(new Date());
      if (result.errors > 0) {
        toast.error(`GA4 sync completed with ${result.errors} error${result.errors > 1 ? 's' : ''}. Check server logs.`);
      } else if (result.rows_upserted === 0 && result.rows_skipped === 0) {
        toast.info('GA4 sync complete — no prospect link clicks found in GA4 yet.');
      } else {
        toast.success(`GA4 sync complete — ${result.rows_upserted} row${result.rows_upserted !== 1 ? 's' : ''} imported.`);
      }
      // Refresh engagement data for all cached sequences
      qc.invalidateQueries({ queryKey: ['click-engagement'] });
    },
    onError: (err: unknown) => {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
          : undefined;
      toast.error(
        typeof detail === 'string' && detail.trim().length > 0
          ? `GA4 sync failed: ${detail}`
          : 'GA4 sync failed. Check that credentials are saved and the service account has GA4 Viewer access.',
      );
    },
  });

  useEffect(() => {
    if (!selectedDraft && filteredDrafts.length > 0) {
      setSelectedDraft(filteredDrafts[0]);
    }
  }, [filteredDrafts, selectedDraft]);

  useEffect(() => {
    if (selectedDraft) {
      const fresh = filteredDrafts.find((d) => d.id === selectedDraft.id);
      if (fresh) setSelectedDraft(fresh);
    }
  }, [filteredDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

  const approveMut = useMutation({
    mutationFn: (id: string) => http.put(`/api/v1/reply-drafts/${id}`, { status: 'approved' }),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
    onError: () => toast.error('Approve failed'),
  });

  const sendMut = useMutation({
    mutationFn: (id: string) => http.post(`/api/v1/reply-drafts/${id}/auto-reply`, { dryRun: false }),
    onSuccess: () => { toast.success('Reply sent!'); setSelectedDraft(null); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
    onError: () => toast.error('Send failed'),
  });

  const dismissMut = useMutation({
    mutationFn: (id: string) => http.put(`/api/v1/reply-drafts/${id}`, { status: 'dismissed' }),
    onSuccess: () => { toast.success('Dismissed'); qc.invalidateQueries({ queryKey: ['reply-drafts'] }); },
    onError: () => toast.error('Dismiss failed'),
  });

  const createDealMut = useMutation({
    mutationFn: (payload: { title: string; value: number; stage: string; notes?: string; prospectId?: string; source: string }) =>
      http.post('/api/v1/deals', payload),
    onSuccess: () => { toast.success('Deal created'); setCreateDealOpen(false); },
    onError: () => toast.error('Failed to create deal'),
  });

  const categorizeMut = useMutation({
    mutationFn: (draft: ReplyDraft) =>
      http.post<CategorizeResult>(`/api/v1/reply-drafts/${draft.id}/reply-categorize`, {
        originalReply: draft.originalReply,
      }),
    onSuccess: (result) => {
      toast.success(`Categorized as: ${getCfg(result.category).label}`);
      qc.invalidateQueries({ queryKey: ['reply-drafts'] });
      qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
    },
    onError: () => toast.error('Categorization failed'),
  });

  const executeActionMut = useMutation({
    mutationFn: (draftId: string) =>
      http.post<ActionResult>(`/api/v1/reply-drafts/${draftId}/execute-action`, {}),
    onSuccess: (_result, draftId) => {
      const draft = replies.find((d) => d.id === draftId);
      const cat = getEffectiveCategory(draft ?? ({} as ReplyDraft));
      const msgs: Record<string, string> = {
        hot_lead:        '🔥 Deal created and draft approved!',
        warm_followup:   '✅ Approved — sequence continues',
        curious:         '✅ Approved — stay in sequence',
        ooo:             '⏰ Prospect snoozed to return date',
        negative:        '🚫 Prospect suppressed from all campaigns',
        bounce:          '⚡ Address handled',
        wrong_company:   '🏢 Prospect marked as no longer there + suppressed',
        unsubscribe:     '🚫 Consent withdrawn — permanently suppressed',
        action_required: '⚠️ Flagged for operator action',
        suppressed:      '🔇 Prospect suppressed',
      };
      toast.success(msgs[cat] ?? 'Action executed');
      qc.invalidateQueries({ queryKey: ['reply-drafts'] });
      qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
    },
    onError: () => toast.error('Action failed'),
  });

  // v4 — handle reclassify confirmation
  const handleReclassifyConfirm = async (category: string, note: string) => {
    if (!selectedDraft) return;
    setReclassifySubmitting(true);
    try {
      const result = await http.post<ReclassifyResult>(
        `/api/v1/reply-drafts/${selectedDraft.id}/reclassify`,
        { category, note: note || null }
      );
      if (result.ok) {
        const cfg = getCfg(category);
        toast.success(`Override applied: ${cfg.label}`);
        if (result.savAlertId) {
          toast.info('⚠️ SAV alert created — check the Action Required banner.');
        }
        qc.invalidateQueries({ queryKey: ['reply-drafts'] });
        qc.invalidateQueries({ queryKey: ['reply-drafts-stats'] });
        qc.invalidateQueries({ queryKey: ['sav-alerts'] });
      } else {
        toast.error(result.message || 'Override failed');
      }
    } catch {
      toast.error('Override failed');
    } finally {
      setReclassifySubmitting(false);
      setReclassifyOpen(false);
    }
  };

  // v4 — acknowledge SAV alert
  const handleAcknowledgeAlert = async (alertId: string, notes: string) => {
    setAcknowledgingAlert(alertId);
    try {
      await http.patch(`/api/v1/reply-drafts/sav-alerts/${alertId}/acknowledge`, { notes: notes || null });
      toast.success('Alert acknowledged');
      refetchSavAlerts();
      refetchStats();
    } catch {
      toast.error('Acknowledge failed');
    } finally {
      setAcknowledgingAlert(null);
    }
  };

  // const handleExportCsv = () => {
  //   const headers = ['id', 'category', 'manualCategory', 'isHumanReviewed', 'status', 'confidence', 'autoPilotEligible', 'prospectName', 'prospectEmail', 'sentEmailSubject', 'originalReply', 'draftBody', 'createdAt'];
  //   const csv = [
  //     headers.join(','),
  //     ...replies.map((r) =>
  //       headers.map((h) => {
  //         const val = String((r as unknown as Record<string, unknown>)[h] ?? '');
  //         return val.includes(',') ? `"${val.replace(/"/g, '""')}"` : val;
  //       }).join(',')
  //     ),
  //   ].join('\n');
  //   const blob = new Blob([csv], { type: 'text/csv' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url; a.download = `reply-drafts-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  //   URL.revokeObjectURL(url);
  //   toast.success('CSV exported');
  // };

  const handleLogReply = async () => {
    if (!logReplyText.trim() || !logProspectId || !logSequenceId) { toast.error('Select a prospect, sequence, and enter reply text'); return; }
    setLogSubmitting(true);
    try {
      await http.post('/api/v1/reply-drafts', { prospectId: logProspectId, sequenceId: logSequenceId, originalReply: logReplyText, category: 'other' });
      toast.success('Reply logged');
      setLogOpen(false); setLogProspectId(''); setLogSequenceId(''); setLogReplyText('');
      qc.invalidateQueries({ queryKey: ['reply-drafts'] });
    } catch { toast.error('Failed to log reply'); }
    finally { setLogSubmitting(false); }
  };

  const handlePreviewAutoPilot = async () => {
    setApPreviewLoading(true); setApPreviewOpen(true);
    try {
      const result = await http.get<AutoPilotPreview>('/api/v1/reply-drafts/auto-pilot');
      setApPreview(result);
    } catch { toast.error('Failed to load preview'); setApPreview({ eligible: [], count: 0 }); }
    finally { setApPreviewLoading(false); }
  };

  const handleRunAutoPilot = async () => {
    setApConfirmOpen(false); setApSending(true);
    try {
      const eligible = apPreview?.eligible ?? autoPilotData?.eligible ?? [];
      const results = await Promise.allSettled(
        eligible.map((d) => http.post(`/api/v1/reply-drafts/${d.id}/auto-reply`, { dryRun: false }))
      );
      const sent = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      setApResult({ sent, failed, markedOnly: 0, total: results.length });
      setApResultOpen(true);
      qc.invalidateQueries({ queryKey: ['reply-drafts'] });
      qc.invalidateQueries({ queryKey: ['reply-drafts-autopilot'] });
      if (sent > 0) toast.success(`Auto-Pilot sent ${sent} draft${sent === 1 ? '' : 's'}`);
      if (failed > 0) toast.error(`${failed} draft${failed === 1 ? '' : 's'} failed`);
    } catch { toast.error('Auto-Pilot run failed'); }
    finally { setApSending(false); }
  };

  const openCreateDeal = async (draft: ReplyDraft) => {
    setDealTitle(''); setDealValue(''); setDealNotes(''); setDealNextAction('');
    setCreateDealOpen(true);
    setDealSuggesting(true);
    try {
      const result = await http.post<{ suggestion?: string; nextAction?: string; dealTitle?: string; dealValue?: number; dealNotes?: string }>(
        `/api/v1/deals/temp/deal-suggest`, { replyDraftId: draft.id }
      );
      setDealTitle(result.dealTitle ?? `${draft.category.replace(/_/g, ' ')} — Deal`);
      setDealValue(String(result.dealValue ?? ''));
      setDealNotes(result.dealNotes ?? result.suggestion ?? '');
      setDealNextAction(result.nextAction ?? '');
    } catch { /* suggestion failed silently */ }
    finally { setDealSuggesting(false); }
  };

  const selectedExtra = useMemo(
    () => parseExtra(selectedDraft?.extraData ?? null),
    [selectedDraft]
  );

  const selectedEffectiveCat = selectedDraft ? getEffectiveCategory(selectedDraft) : '';
  const selectedCfg = selectedDraft ? getCfg(selectedEffectiveCat) : null;
  const unacknowledgedSavAlerts = inboxStats?.unacknowledgedSavAlerts ?? 0;
  const savAlertsList = savAlertsData?.alerts ?? [];

  return (
    <div className="space-y-4">

      {/* Per-user: which mailbox / domain this user's replies are sent from */}
      <SendingDomainNotice compact />

      {/* ── SAV Alert Banner ──────────────────────────────────────────── */}
      <SAVAlertBanner
        count={unacknowledgedSavAlerts}
        onViewAlerts={() => setSavAlertsOpen(true)}
      />

      {/* ── GA4 Click Engagement Sync Strip (hidden while SHOW_GA4_UI = false) ── */}
      {SHOW_GA4_UI && (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 px-4 py-2.5 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <span className="text-xs font-medium text-blue-900 dark:text-blue-200">GA4 Click Engagement</span>
          {ga4Enabled ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400">
              <Wifi className="h-2.5 w-2.5" /> Enabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-muted-foreground/30 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              <WifiOff className="h-2.5 w-2.5" /> Disabled
            </span>
          )}
          {ga4LastSync && (
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              · Last synced {ga4LastSync.toLocaleTimeString()}
              {ga4SyncResult && ga4SyncResult.rows_upserted > 0 && (
                <span className="ml-1 text-green-700 font-medium">({ga4SyncResult.rows_upserted} rows)</span>
              )}
            </span>
          )}
          {!ga4Enabled && ga4Params && (
            <span className="text-[11px] text-amber-700 hidden md:inline">
              · Configure ga4.* params in System Parameters to enable
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
          onClick={() => ga4SyncMut.mutate()}
          disabled={ga4SyncMut.isPending || !ga4Enabled || !ga4HasPropertyId}
          title={!ga4Enabled ? 'Enable GA4 in System Parameters first' : !ga4HasPropertyId ? 'Set ga4.property_id in System Parameters first' : 'Pull latest GA4 engagement data now'}
        >
          {ga4SyncMut.isPending ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Syncing…</>
          ) : (
            <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Sync GA4 Now</>
          )}
        </Button>
      </div>
      )}

      {/* ── Tracking Dashboard Strip ──────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground">Email Activity Overview</h2>
        <Select value={String(trackingDays)} onValueChange={(v) => setTrackingDays(Number(v))}>
          <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[7, 14, 30, 60, 90].map(d => (
              <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {trackingLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0,1,2,3].map(i => <div key={i} className="rounded-lg border p-4 animate-pulse bg-muted/30 h-20" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Sent */}
          <Card className="border bg-blue-50/50 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1 text-blue-700">
                <Mail className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-medium">Sent</span>
              </div>
              <p className="text-xl font-bold text-blue-700">{(trackingSummary?.total_sent ?? 0).toLocaleString()}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Last {trackingDays} days</p>
            </CardContent>
          </Card>

          {/* Replied */}
          <Card className="border bg-emerald-50/50 border-emerald-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1 text-emerald-700">
                <MessageCircleReply className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-xs font-medium">Replied</span>
              </div>
              <p className="text-xl font-bold text-emerald-700">{(trackingSummary?.total_replied ?? 0).toLocaleString()}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{trackingSummary ? `${(trackingSummary.reply_rate * 100).toFixed(1)}% rate` : '—'}</p>
            </CardContent>
          </Card>

          {/* Bounced — rate + reasons on one line, benchmark badge in header */}
          {(() => {
            const bounceRate = trackingSummary?.bounce_rate ?? 0;
            const totalBounced = trackingSummary?.total_bounced ?? 0;
            const isHigh = bounceRate > 0.05;
            const reasons = trackingSummary?.top_bounce_reasons ?? [];
            return (
              <Card className={cn(
                'border col-span-2 md:col-span-1',
                isHigh ? 'bg-red-50/50 border-red-200' : 'bg-orange-50/50 border-orange-200'
              )}>
                <CardContent className="p-3">
                  <div className={cn('flex items-center gap-1.5 mb-1', isHigh ? 'text-red-700' : 'text-orange-700')}>
                    <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                    <span className="text-xs font-medium">Bounced</span>
                    {isHigh && (
                      <span className="ml-auto text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-0.5 shrink-0">
                        &gt;5% benchmark
                      </span>
                    )}
                  </div>
                  <p className={cn('text-xl font-bold', isHigh ? 'text-red-700' : 'text-orange-700')}>
                    {totalBounced.toLocaleString()}
                  </p>
                  <div className="flex items-center flex-wrap gap-1 mt-0.5">
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {trackingSummary ? `${(bounceRate * 100).toFixed(1)}% rate` : '—'}
                    </span>
                    {reasons.map(r => (
                      <span key={r.reason} className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-orange-100 text-orange-800 text-[10px] border border-orange-200 leading-4">
                        <span className="font-semibold">{r.count}×</span>
                        <span className="truncate max-w-[70px]">{r.reason}</span>
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Link Clicked */}
          <Card className="border bg-violet-50/50 border-violet-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1 text-violet-700">
                <MousePointerClick className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-xs font-medium">Link Clicked</span>
              </div>
              <p className="text-xl font-bold text-violet-700">{uniqueClickGroups.length.toLocaleString()}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Unique links clicked</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="manual">
            <MessageCircleReply className="h-3.5 w-3.5 mr-1.5" /> Manual Review
          </TabsTrigger>
          <TabsTrigger value="autopilot">
            <Bot className="h-3.5 w-3.5 mr-1.5" /> Auto-Pilot
            {autoPilotStats.eligible > 0 && (
              <Badge className="ml-1.5 bg-violet-100 text-violet-700 border-violet-200 text-[10px] px-1 py-0 border">
                {autoPilotStats.eligible}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bounced">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Bounced
            {bouncedSequences.length > 0 && (
              <Badge className="ml-1.5 bg-red-100 text-red-700 border-red-200 text-[10px] px-1 py-0 border">
                {bouncedSequences.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="message-blocked">
            <ShieldX className="h-3.5 w-3.5 mr-1.5" /> Message Blocked
            {messageBlockedSequences.length > 0 && (
              <Badge className="ml-1.5 bg-orange-100 text-orange-700 border-orange-200 text-[10px] px-1 py-0 border">
                {messageBlockedSequences.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="clicked">
            <MousePointerClick className="h-3.5 w-3.5 mr-1.5" /> Link Clicked
            {uniqueClickGroups.length > 0 && (
              <Badge className="ml-1.5 bg-violet-100 text-violet-700 border-violet-200 text-[10px] px-1 py-0 border">
                {uniqueClickGroups.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Manual Review Tab ─────────────────────────────────────────── */}
        <TabsContent value="manual" className="space-y-4 mt-4">

          {/* Reply summary — keep only the two key inbox metrics */}
          {associationFilteredReplies.length > 0 && (() => {
            const positiveReplies = associationFilteredReplies.filter((reply) => {
              const category = getEffectiveCategory(reply);
              const normalised = LEGACY_CATEGORY_DISPLAY[category] ?? category;
              return normalised === 'hot_lead' || normalised === 'warm_followup';
            }).length;
            const positiveRate = (positiveReplies / associationFilteredReplies.length) * 100;
            return (
              <div className="flex flex-wrap gap-4 items-center p-3 rounded-lg border bg-muted/20">
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{associationFilteredReplies.length}</span> Total Replies
                </div>
                <div className={cn('text-xs font-medium', positiveRate >= 5 ? 'text-emerald-700' : 'text-amber-700')}>
                  {Math.round(positiveRate)}% Positive Rate
                </div>
              </div>
            );
          })()}

          {/* Category filter pills */}
          <div className="flex flex-wrap gap-1.5">
            {FILTER_PILLS.map(({ key, label }) => {
              const count = key === 'all'
                ? associationFilteredReplies.length
                : associationFilteredReplies.filter((r) => {
                    const effectiveCat = getEffectiveCategory(r);
                    const n = LEGACY_CATEGORY_DISPLAY[effectiveCat] ?? effectiveCat;
                    // Match on effective category only — do NOT use r.category as fallback,
                    // which would count re-classified replies under their old category.
                    return n === key || effectiveCat === key;
                  }).length;
              return (
                <button
                  key={key}
                  onClick={() => { setCategoryFilter(key); setSelectedDraft(null); }}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    categoryFilter === key
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                  )}
                >
                  {label}
                  <span className="rounded-full bg-black/10 dark:bg-white/10 px-1 text-[10px]">{count}</span>
                </button>
              );
            })}
          </div>

          {/* ── Association filters: whose replies / campaign / mailbox ── */}
          <div className="flex items-center gap-2 flex-wrap">
            {isManager && (
              <Select value={replyScope} onValueChange={(v) => { setReplyScope(v as 'all' | 'mine'); setSelectedDraft(null); }}>
                <SelectTrigger className="h-8 w-40 text-xs" aria-label="Whose replies"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users&apos; Replies</SelectItem>
                  <SelectItem value="mine">My Replies</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={campaignFilter} onValueChange={(v) => { setCampaignFilter(v); setSelectedDraft(null); }}>
              <SelectTrigger className="h-8 w-52 text-xs" aria-label="Campaign"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value={ALL_FILTER}>All Campaigns</SelectItem>
                {campaignOptions.map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={mailboxFilter} onValueChange={(v) => { setMailboxFilter(v); setSelectedDraft(null); }}>
              <SelectTrigger className="h-8 w-60 text-xs" aria-label="Received in mailbox"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value={ALL_FILTER}>All Mailboxes</SelectItem>
                {mailboxOptions.map(([key, o]) => (
                  <SelectItem key={key} value={key}>
                    {o.email}{o.registered === false ? ' (domain not registered)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isManager && (
              <span className="text-xs text-muted-foreground">Showing replies to your emails</span>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {pendingCount > 0 && <Badge variant="default" className="text-xs">{pendingCount} pending</Badge>}
            <span className="text-xs text-muted-foreground">Auto-refreshes every 30s</span>
            {/* <Button variant="outline" size="sm" onClick={() => setLogOpen(true)}>
              <MessageCircleReply className="h-4 w-4 mr-1" /> Log a reply
            </Button> */}
            {/* <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={replies.length === 0}>
              <FileDown className="h-4 w-4 mr-1" /> Export CSV
            </Button> */}
            <div className="ml-auto flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
              <Switch id="filter-ap" checked={showAutoPilotOnly} onCheckedChange={setShowAutoPilotOnly} />
              <Label htmlFor="filter-ap" className="text-xs font-medium cursor-pointer flex items-center gap-1">
                <Zap className="h-3 w-3 text-violet-600" /> Show Only Auto-Pilot Eligible Drafts
              </Label>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* ── Left: Draft List ─────────────────────────────────────── */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>
                    Reply Drafts
                    {showAutoPilotOnly && (
                      <Badge variant="outline" className="ml-2 text-[10px]">Filter: Auto-Pilot Eligible</Badge>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground font-normal">{filteredDrafts.length} shown</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[640px] overflow-y-auto">
                  {isLoading ? (
                    <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
                  ) : filteredDrafts.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      {showAutoPilotOnly
                        ? 'No Auto-Pilot Eligible Drafts.'
                        : categoryFilter !== 'all'
                        ? 'No replies in this category.'
                        : 'No reply drafts yet. When prospects reply, they appear here automatically.'
                      }
                    </div>
                  ) : filteredDrafts.map((d) => {
                    const effectiveCat = getEffectiveCategory(d);
                    return (
                      <button
                        key={d.id}
                        onClick={() => setSelectedDraft(d)}
                        className={cn(
                          'w-full text-left p-3 border-b last:border-0 hover:bg-muted/30 transition-colors',
                          selectedDraft?.id === d.id && 'bg-muted/50',
                          // v4: left border colour by effective category
                          effectiveCat === 'action_required'
                            ? 'border-l-2 border-l-amber-400'
                            : effectiveCat === 'suppressed'
                            ? 'border-l-2 border-l-red-400'
                            : d.isHumanReviewed
                            ? 'border-l-2 border-l-blue-400'
                            : selectedDraft?.id === d.id
                            ? 'border-l-2 border-l-primary'
                            : ''
                        )}
                      >
                        {/* Prospect name + status badge */}
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold truncate">
                            {d.prospectName ?? d.prospectEmail ?? 'Unknown Prospect'}
                          </span>
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] shrink-0', STATUS_COLORS[d.status] ?? 'bg-gray-100')}>
                            {DRAFT_STATUS_LABEL[d.status] ?? d.status}
                          </span>
                        </div>

                        {/* Category badge (uses CategoryBadge with tooltip for human-reviewed) */}
                        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                          <CategoryBadge draft={d} size="xs" />
                          {d.autoPilotEligible && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 flex items-center gap-0.5">
                              <Zap className="h-2.5 w-2.5" /> auto-pilot
                            </span>
                          )}
                        </div>

                        {d.sentEmailSubject && (
                          <p className="text-[10px] text-muted-foreground mb-0.5 truncate">
                            📧 {d.sentEmailSubject}
                          </p>
                        )}
                        {associationLine(d) && (
                          <p className="text-[10px] text-muted-foreground mb-0.5 truncate" title={associationLine(d)}>
                            {associationLine(d)}
                          </p>
                        )}

                        <p className="text-xs text-foreground/80 line-clamp-2 leading-snug">{d.originalReply}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(d.createdAt).toLocaleString()}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* ── Right: Conversation Thread + Actions ─────────────────── */}
            <Card className="lg:col-span-3">
              {!selectedDraft ? (
                <CardContent className="flex items-center justify-center h-full min-h-[200px]">
                  <p className="text-sm text-muted-foreground">Select a reply to view the conversation thread.</p>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="pb-3 border-b">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <CardTitle className="text-base">
                          {selectedDraft.prospectName ?? selectedDraft.prospectEmail ?? 'Unknown Prospect'}
                        </CardTitle>
                        {selectedDraft.prospectEmail && (
                          <p className="text-xs text-muted-foreground mt-0.5">{selectedDraft.prospectEmail}</p>
                        )}
                        {/* Association: campaign, touch, inbox it arrived in, sending domain */}
                        <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                          {selectedDraft.campaignName && (
                            <>
                              <span className="text-muted-foreground">Campaign</span>
                              <span className="font-medium">
                                {selectedDraft.campaignName}
                                {selectedDraft.touchNumber ? ` · Touch ${selectedDraft.touchNumber}` : ''}
                              </span>
                            </>
                          )}
                          {selectedDraft.sentFromEmail && (
                            <>
                              <span className="text-muted-foreground">Received In</span>
                              <span className="font-medium">{selectedDraft.sentFromEmail}</span>
                            </>
                          )}
                          {selectedDraft.sendingDomain && (
                            <>
                              <span className="text-muted-foreground">Sending Domain</span>
                              <span className="flex items-center gap-1.5">
                                {selectedDraft.sendingDomain}
                                {selectedDraft.sendingDomainRegistered === false && (
                                  <Badge variant="warning" className="text-[10px]">Not Registered</Badge>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Category badge with tooltip */}
                        <CategoryBadge draft={selectedDraft} size="sm" />
                        <span className={cn('px-2 py-0.5 rounded text-xs', STATUS_COLORS[selectedDraft.status] ?? 'bg-gray-100')}>
                          {DRAFT_STATUS_LABEL[selectedDraft.status] ?? selectedDraft.status}
                        </span>
                        {selectedDraft.autoPilotEligible && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 flex items-center gap-1">
                            <Zap className="h-3 w-3" /> Auto-Pilot Eligible
                          </span>
                        )}
                        {selectedDraft.confidence != null && (
                          <span className="text-xs text-muted-foreground">
                            {Math.round(selectedDraft.confidence * 100)}% confidence
                          </span>
                        )}
                        {/* Re-classify AI button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => categorizeMut.mutate(selectedDraft)}
                          disabled={categorizeMut.isPending || !!selectedDraft.manualCategory}
                          title={selectedDraft.manualCategory ? 'Category is human-locked — clear the override to re-classify' : 'Re-run AI categorization'}
                        >
                          <RefreshCw className={cn('h-3 w-3 mr-1', categorizeMut.isPending && 'animate-spin')} />
                          Re-classify
                        </Button>
                        {/* v4: Override Category button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setReclassifyOpen(true)}
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Override
                        </Button>
                      </div>
                    </div>
                    {selectedDraft.summary && (
                      <p className="text-xs text-muted-foreground mt-1 italic">{selectedDraft.summary}</p>
                    )}
                    {/* v4: show human override note if set */}
                    {selectedDraft.manualCategoryNote && (
                      <div className="mt-1.5 rounded border border-blue-200 bg-blue-50 px-2 py-1.5 flex items-start gap-1.5">
                        <Edit2 className="h-3 w-3 text-blue-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-800">
                          <span className="font-semibold">Override note:</span> {selectedDraft.manualCategoryNote}
                        </p>
                      </div>
                    )}
                  </CardHeader>

                  <CardContent className="pt-4">
                    <div className="space-y-4">

                      {/* action_required: SAV panel */}
                      {selectedEffectiveCat === 'action_required' && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <TriangleAlert className="h-4 w-4 text-amber-700" />
                            <p className="text-sm font-semibold text-amber-800">
                              Sender Address Verification — Operator Action Required
                            </p>
                          </div>
                          <p className="text-xs text-amber-700">
                            This email is an infrastructure challenge from a mail server, not a prospect reply.
                            No prospect action has been taken. Review the alert and acknowledge it once resolved.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 text-amber-800 hover:bg-amber-100"
                            onClick={() => setSavAlertsOpen(true)}
                          >
                            <TriangleAlert className="h-3.5 w-3.5 mr-1" /> View Alerts
                          </Button>
                        </div>
                      )}

                      {/* AI suggested action panel */}
                      {selectedDraft.suggestedAction && selectedCfg && selectedEffectiveCat !== 'action_required' && (
                        <div className={cn('rounded-lg border p-3 flex items-start gap-2', selectedCfg.bgColor, selectedCfg.borderColor)}>
                          <Sparkles className={cn('h-4 w-4 shrink-0 mt-0.5', selectedCfg.textColor)} />
                          <div>
                            <p className={cn('text-xs font-semibold mb-0.5', selectedCfg.textColor)}>AI Recommended Action</p>
                            <p className="text-sm">{selectedDraft.suggestedAction}</p>
                          </div>
                        </div>
                      )}

                      {/* wrong_company special panel */}
                      {selectedEffectiveCat === 'wrong_company' && (
                        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-purple-700" />
                            <p className="text-sm font-semibold text-purple-700">Prospect no longer works there</p>
                          </div>
                          {selectedExtra.newCompany && (
                            <p className="text-sm"><span className="font-medium">Now at:</span> {selectedExtra.newCompany}</p>
                          )}
                          {selectedExtra.referralName && (
                            <p className="text-sm"><span className="font-medium">Referred to:</span> {selectedExtra.referralName}</p>
                          )}
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-300 text-purple-700 hover:bg-purple-100"
                              onClick={() => toast.info('Search for replacement via LinkedIn Hub or Datamoon')}
                            >
                              <Search className="h-3.5 w-3.5 mr-1" /> Find Replacement
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* OOO snooze info panel */}
                      {selectedEffectiveCat === 'ooo' && (
                        <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-sky-600 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-sky-800">
                              {selectedExtra.returnDate
                                ? `Return date: ${new Date(selectedExtra.returnDate).toLocaleDateString()}`
                                : 'Return date unknown — defaulting to 2 weeks'}
                            </p>
                            {selectedDraft.snoozedUntil && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Sequence will resume {new Date(selectedDraft.snoozedUntil).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <ConversationThread draft={selectedDraft} />

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap pt-2 border-t">
                        {selectedDraft.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => approveMut.mutate(selectedDraft.id)} disabled={approveMut.isPending}>
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => sendMut.mutate(selectedDraft.id)} disabled={sendMut.isPending}>
                              <Send className="h-4 w-4 mr-1" />{sendMut.isPending ? 'Sending…' : 'Send Now'}
                            </Button>

                            {selectedCfg && selectedEffectiveCat !== 'suppressed' && (
                              <Button
                                size="sm"
                                variant={selectedCfg.actionVariant}
                                onClick={() => executeActionMut.mutate(selectedDraft.id)}
                                disabled={executeActionMut.isPending}
                                className={cn(
                                  selectedEffectiveCat === 'wrong_company' && 'border-purple-300 text-purple-700 hover:bg-purple-50',
                                  selectedEffectiveCat === 'ooo' && 'border-sky-300 text-sky-700 hover:bg-sky-50',
                                  selectedEffectiveCat === 'action_required' && 'border-amber-400 text-amber-800 hover:bg-amber-50',
                                )}
                              >
                                {executeActionMut.isPending
                                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                  : selectedCfg.icon
                                }
                                {selectedCfg.actionLabel}
                              </Button>
                            )}

                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                              onClick={() => dismissMut.mutate(selectedDraft.id)} disabled={dismissMut.isPending}>
                              <Ban className="h-4 w-4 mr-1" /> Dismiss
                            </Button>
                          </>
                        )}
                        {selectedDraft.status === 'approved' && (
                          <Button size="sm" onClick={() => sendMut.mutate(selectedDraft.id)} disabled={sendMut.isPending}>
                            <Send className="h-4 w-4 mr-1" />{sendMut.isPending ? 'Sending…' : 'Send Now'}
                          </Button>
                        )}
                        {(selectedDraft.status === 'sent' || selectedDraft.status === 'auto_sent') && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {selectedDraft.status === 'auto_sent' ? 'Auto-Sent' : 'Sent'}
                          </Badge>
                        )}
                        {selectedDraft.status === 'handled' && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Handled
                          </Badge>
                        )}
                        {POSITIVE_CATEGORIES.includes(selectedEffectiveCat) && (
                          <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => openCreateDeal(selectedDraft)}>
                            <DollarSign className="h-4 w-4 mr-1" />
                            {dealSuggesting && createDealOpen ? 'Analyzing…' : 'Create Deal'}
                          </Button>
                        )}
                      </div>

                      {/* Playbook sidebar hint */}
                      {selectedCfg && selectedCfg.playbook.length > 0 && (
                        <div className={cn('rounded-lg border p-3 text-xs space-y-1.5', selectedCfg.bgColor, selectedCfg.borderColor)}>
                          <p className={cn('font-semibold', selectedCfg.textColor)}>Playbook — {selectedCfg.label}</p>
                          <ul className="space-y-0.5 text-muted-foreground">
                            {selectedCfg.playbook.map((step, i) => (
                              <li key={i}>• {step}</li>
                            ))}
                          </ul>
                          {selectedEffectiveCat === 'unsubscribe' && (
                            <p className="text-rose-700 font-medium flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" /> This action is permanent and irrevocable.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Inline deal form */}
                      {createDealOpen && (
                        <Card className="border-emerald-200 bg-emerald-50/50">
                          <CardContent className="p-3 space-y-2">
                            <p className="text-xs font-medium flex items-center gap-1">
                              <Sparkles className="h-3 w-3" /> AI-Suggested Deal
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Deal Title</Label>
                                <Input className="h-8 text-sm mt-1" value={dealTitle} onChange={(e) => setDealTitle(e.target.value)} />
                              </div>
                              <div>
                                <Label className="text-xs">Value ($)</Label>
                                <Input className="h-8 text-sm mt-1" type="number" placeholder="0" value={dealValue} onChange={(e) => setDealValue(e.target.value)} />
                              </div>
                            </div>
                            {dealNotes && (
                              <div>
                                <Label className="text-xs">AI Notes</Label>
                                <p className="text-xs text-muted-foreground mt-1 bg-white rounded p-2 border">{dealNotes}</p>
                              </div>
                            )}
                            {dealNextAction && (
                              <div>
                                <Label className="text-xs">Suggested Next Action</Label>
                                <p className="text-xs text-emerald-800 mt-1 bg-white rounded p-2 border border-emerald-200">{dealNextAction}</p>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button size="sm" disabled={!dealTitle || createDealMut.isPending}
                                onClick={() => createDealMut.mutate({ title: dealTitle, value: parseFloat(dealValue) || 0, stage: 'qualified', notes: dealNotes || undefined, prospectId: selectedDraft?.prospectId, source: 'cold_email' })}>
                                {createDealMut.isPending ? 'Creating…' : 'Create Deal'}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setCreateDealOpen(false)}>Cancel</Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ── Auto-Pilot Tab ────────────────────────────────────────────── */}
        <TabsContent value="autopilot" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-violet-200 bg-violet-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-violet-700"><Zap className="h-4 w-4" /><span className="text-xs font-medium">Eligible</span></div>
                <p className="text-2xl font-bold mt-1">{autoPilotStats.eligible}</p>
                <p className="text-[11px] text-muted-foreground">Pending + Auto-Pilot Eligible</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-medium">Auto-Sent</span></div>
                <p className="text-2xl font-bold mt-1">{autoPilotStats.autoSent}</p>
                <p className="text-[11px] text-muted-foreground">Already sent via Auto-Pilot</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-700"><Clock className="h-4 w-4" /><span className="text-xs font-medium">Pending Review</span></div>
                <p className="text-2xl font-bold mt-1">{autoPilotStats.pendingReview}</p>
                <p className="text-[11px] text-muted-foreground">All drafts awaiting your action</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground"><MessageCircleReply className="h-4 w-4" /><span className="text-xs font-medium">Total Drafts</span></div>
                <p className="text-2xl font-bold mt-1">{autoPilotStats.total}</p>
                <p className="text-[11px] text-muted-foreground">All reply drafts</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-white">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Bot className="h-4 w-4 text-violet-600" /> Auto-Pilot Reply Sender</CardTitle>
              <CardDescription>
                Auto-send high-confidence positive replies. Drafts with category <code className="text-[10px] bg-muted px-1 py-0.5 rounded">hot_lead</code>, <code className="text-[10px] bg-muted px-1 py-0.5 rounded">warm_followup</code> with confidence ≥ 80% are eligible.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handlePreviewAutoPilot} disabled={apPreviewLoading}>
                  {apPreviewLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  Preview Auto-Pilot
                </Button>
                <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                  onClick={() => { if (autoPilotStats.eligible === 0) { toast.info('No eligible drafts to auto-send.'); return; } setApConfirmOpen(true); }}
                  disabled={apSending}>
                  {apSending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                  Run Auto-Pilot ({autoPilotStats.eligible} draft{autoPilotStats.eligible === 1 ? '' : 's'})
                </Button>
              </div>
              {apSending && (
                <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Auto-Pilot is sending eligible drafts via MailBridge…
                </div>
              )}
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
                <p className="font-medium flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> How Auto-Pilot works</p>
                <ol className="list-decimal list-inside space-y-0.5 ml-1">
                  <li>Click <b>Preview Auto-Pilot</b> to dry-run — see exactly which drafts would be sent.</li>
                  <li>Click <b>Run Auto-Pilot</b> to actually send. You'll get a confirmation dialog first.</li>
                  <li>If MailBridge is connected, drafts are sent via your sending domain. Otherwise, drafts are marked <code className="text-[10px] bg-white px-1 py-0.5 rounded">auto_sent</code> but not delivered.</li>
                  <li>Failed sends stay in <code className="text-[10px] bg-white px-1 py-0.5 rounded">pending</code> so you can retry manually.</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Bounced Tab ───────────────────────────────────────────────── */}
        <TabsContent value="bounced" className="mt-4">
          {bouncedSequences.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-3" />
              <p className="text-sm font-medium">No bounced emails</p>
              <p className="text-xs text-muted-foreground mt-1">
                Bounces are detected automatically by the reply poller every 2 minutes.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {bouncedSequences.length} bounced sequence{bouncedSequences.length === 1 ? '' : 's'} — these addresses could not be delivered to. Affected prospects are suppressed from future sends.
              </p>
              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Subject</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Bounce Reason</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Bounced At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bouncedSequences.map((seq, idx) => (
                      <tr key={seq.id} className={cn('border-b last:border-0', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="p-3 font-medium">Touch {seq.touchNumber}</td>
                        <td className="p-3">
                          {seq.subjectLine
                            ? <span className="truncate max-w-[200px] block">{seq.subjectLine}</span>
                            : <span className="text-muted-foreground italic">—</span>}
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] border border-red-200">
                            {seq.bounceReason ?? 'unknown'}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {seq.bouncedAt ? new Date(seq.bouncedAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Message Blocked Tab ────────────────────────────────────── */}
        <TabsContent value="message-blocked" className="mt-4">
          {messageBlockedSequences.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-3" />
              <p className="text-sm font-medium">No blocked emails</p>
              <p className="text-xs text-muted-foreground mt-1">
                Messages blocked by security gateways (e.g. Mimecast, Barracuda) will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {messageBlockedSequences.length} blocked sequence{messageBlockedSequences.length === 1 ? '' : 's'} — these emails were rejected by the recipient&apos;s security gateway. Affected prospects are suppressed from future sends.
              </p>
              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Subject</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Block Reason</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Blocked At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messageBlockedSequences.map((seq, idx) => (
                      <tr key={seq.id} className={cn('border-b last:border-0', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="p-3 font-medium">Touch {seq.touchNumber}</td>
                        <td className="p-3">
                          {seq.subjectLine
                            ? <span className="truncate max-w-[200px] block">{seq.subjectLine}</span>
                            : <span className="text-muted-foreground italic">—</span>}
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] border border-orange-200">
                            {seq.messageBlockedReason ?? 'unknown'}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {seq.messageBlockedAt ? new Date(seq.messageBlockedAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Link Clicked Tab ─────────────────────────────────────────── */}
        <TabsContent value="clicked" className="mt-4">
          {uniqueClickGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MousePointerClick className="h-8 w-8 text-violet-400 mb-3" />
              <p className="text-sm font-medium">No link clicks yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Clicks on tracked links in your emails will show up here as they come in.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Phase C: scanner toggle + count line */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {uniqueClickGroups.length} unique link{uniqueClickGroups.length === 1 ? '' : 's'} clicked — most recent first.
                  {!showScanners && (
                    <span className="ml-1 text-muted-foreground/70">(human clicks only)</span>
                  )}
                </p>
                <button
                  onClick={() => setShowScanners((v) => !v)}
                  className={cn(
                    'text-[11px] px-2 py-1 rounded border transition-colors',
                    showScanners
                      ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                      : 'bg-muted border-muted-foreground/20 text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {showScanners ? 'Hide scanners & bots' : 'Show suspected scanners & bots'}
                </button>
              </div>

              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-muted-foreground">Touch</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Prospect</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Last Clicked</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Original Link</th>
                      {SHOW_GA4_UI && (
                        <th className="text-left p-3 font-medium text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Activity className="h-3 w-3" /> GA4 Engagement
                          </span>
                        </th>
                      )}
                      {showScanners && (
                        <th className="text-left p-3 font-medium text-muted-foreground">Quality</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueClickGroups.map(({ data: c, count }, idx) => {
                      const isScanner = c.clickQuality && c.clickQuality !== 'human';
                      return (
                        <tr
                          key={`${c.touchNumber}|${c.prospectEmail}|${c.originalUrl}`}
                          className={cn(
                            'border-b last:border-0',
                            isScanner ? 'opacity-50' : (idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')
                          )}
                        >
                          <td className="p-3 font-medium">
                            {c.touchNumber ? `Touch ${c.touchNumber}` : <span className="text-muted-foreground italic">—</span>}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              {c.prospectName?.trim()
                                ? <span className="truncate max-w-[180px] block">{c.prospectName}</span>
                                : <span className="text-muted-foreground italic">—</span>}
                              {count > 1 && (
                                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold border border-violet-200 shrink-0">
                                  {count}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {c.prospectEmail
                              ? <span className="truncate max-w-[220px] block">{c.prospectEmail}</span>
                              : <span className="text-muted-foreground italic">—</span>}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {c.clickedAt ? new Date(c.clickedAt).toLocaleString() : '—'}
                          </td>
                          <td className="p-3">
                            {c.originalUrl ? (
                              <a
                                href={c.originalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 hover:underline"
                                title={c.originalUrl}
                              >
                                <span className="truncate max-w-[200px]">{c.originalUrl}</span>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground italic">—</span>
                            )}
                          </td>
                          {SHOW_GA4_UI && <EngagementCell sequenceId={c.sequenceId} />}
                          {showScanners && (
                            <td className="p-3">
                              {c.clickQuality === 'likely_scanner' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                  Suspected Scanner
                                </span>
                              )}
                              {c.clickQuality === 'bot' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                  Bot
                                </span>
                              )}
                              {(!c.clickQuality || c.clickQuality === 'human') && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
                                  human
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Log a Reply Dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log a Reply</DialogTitle>
            <DialogDescription>Paste an external reply that wasn't captured by MailBridge.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Prospect <span className="text-destructive">*</span></Label>
              <Select value={logProspectId} onValueChange={(v) => { setLogProspectId(v); setLogSequenceId(''); }}>
                <SelectTrigger><SelectValue placeholder="Select prospect…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {prospects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}{p.company ? ` — ${p.company}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Sequence (Touch) <span className="text-destructive">*</span></Label>
              <Select value={logSequenceId} onValueChange={setLogSequenceId} disabled={!logProspectId}>
                <SelectTrigger><SelectValue placeholder={logProspectId ? 'Select sequence…' : 'Select a prospect first'} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {logSequences.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      Touch {s.touchNumber} — {s.angle.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Reply Text <span className="text-destructive">*</span></Label>
              <Textarea rows={5} value={logReplyText} onChange={(e) => setLogReplyText(e.target.value)} placeholder="Paste the prospect's reply here…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
            <Button disabled={!logReplyText.trim() || !logProspectId || !logSequenceId || logSubmitting} onClick={handleLogReply}>
              {logSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Log Reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Pilot Preview Dialog */}
      <Dialog open={apPreviewOpen} onOpenChange={setApPreviewOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-violet-600" /> Auto-Pilot Preview (Dry-Run)</DialogTitle>
            <DialogDescription>
              {apPreviewLoading ? 'Loading…' : `${apPreview?.count ?? 0} draft${(apPreview?.count ?? 0) === 1 ? '' : 's'} would be sent.`}
            </DialogDescription>
          </DialogHeader>
          {apPreviewLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-2">
              {(apPreview?.eligible ?? []).length === 0
                ? <p className="text-sm text-muted-foreground text-center py-4">No eligible drafts.</p>
                : (apPreview?.eligible ?? []).map((d) => (
                  <div key={d.id} className="p-3 rounded-lg border text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', CATEGORY_COLORS[getEffectiveCategory(d)] ?? 'bg-gray-100')}>
                          {getCfg(getEffectiveCategory(d)).label}
                        </span>
                        <span className="text-xs font-medium">{d.prospectName ?? d.prospectEmail ?? 'Unknown'}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{d.confidence != null ? `${Math.round(d.confidence * 100)}% confidence` : ''}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{d.draftBody ?? d.originalReply}</p>
                  </div>
                ))
              }
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApPreviewOpen(false)}>Close</Button>
            <Button onClick={() => { setApPreviewOpen(false); setApConfirmOpen(true); }} disabled={(apPreview?.count ?? 0) === 0}>
              Proceed to Send All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Pilot Confirm Dialog */}
      <Dialog open={apConfirmOpen} onOpenChange={setApConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> Confirm Auto-Pilot Send</DialogTitle>
            <DialogDescription>
              This will send <strong>{autoPilotStats.eligible}</strong> eligible draft{autoPilotStats.eligible === 1 ? '' : 's'} via MailBridge. Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleRunAutoPilot} disabled={apSending}>
              {apSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Run Auto-Pilot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Pilot Result Dialog */}
      <Dialog open={apResultOpen} onOpenChange={setApResultOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(apResult?.failed ?? 0) === 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              Auto-Pilot Results
            </DialogTitle>
            <DialogDescription>Send run complete.</DialogDescription>
          </DialogHeader>
          {apResult && (
            <div className="grid grid-cols-3 gap-4 py-2">
              {[
                { label: 'Sent', value: apResult.sent, color: 'text-emerald-600' },
                { label: 'Failed', value: apResult.failed, color: 'text-red-600' },
                { label: 'Total', value: apResult.total, color: 'text-foreground' },
              ].map((s) => (
                <div key={s.label} className="text-center p-3 rounded-lg border">
                  <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={() => setApResultOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v4: Reclassify Modal */}
      <ReclassifyModal
        open={reclassifyOpen}
        draft={selectedDraft}
        onClose={() => setReclassifyOpen(false)}
        onConfirm={handleReclassifyConfirm}
        submitting={reclassifySubmitting}
      />

      {/* v4: SAV Alerts Modal */}
      <SAVAlertsModal
        open={savAlertsOpen}
        onClose={() => setSavAlertsOpen(false)}
        alerts={savAlertsList}
        onAcknowledge={handleAcknowledgeAlert}
        acknowledging={acknowledgingAlert}
      />
    </div>
  );
}